// Enumerations stored as `text` + `CHECK (col IN (...))` (13 §Conventions, fix #4).
// These tuples are the single source: the CHECK constraints are generated from them and the
// zod contracts (S1-03) derive their enums from them. Adding a value means a migration.

// cp
export const agencyGrantLevel = ["operator", "viewer"] as const;
export const agencyUserRole = ["operator", "admin"] as const;
export const billingInterval = ["month", "year"] as const;
/**
 * WF-CONTROL: `workforce_plugin` (Workforce Control: manage the digital workforce from Claude
 * Code; requires `agents`; a flat add-on, no billing logic in phase 1).
 */
export const addonKey = [
  "kpi",
  "memory",
  "agents",
  "chat",
  "time",
  "media",
  "workforce_plugin",
] as const;

/**
 * Currencies the slice accepts. All have two minor-unit decimals (ISO 4217 exponent 2), which
 * is what `amount_minor` and the micros → minor rule (10^4 micros per minor) assume. Adding a
 * currency with another exponent needs a currency-aware conversion first.
 */
export const supportedCurrency = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "NZD",
  "CHF",
  "SGD",
  "PHP",
  "MXN",
  "INR",
  "ZAR",
] as const;
export const addonDependencyMode = ["requires", "bundles"] as const;
export const entitlementStatus = ["active", "trial", "grace", "disabled"] as const;
/**
 * PROVISION-IN-PRODUCT: `rule` — switched on by a feature rule (the company's plan, a connected
 * integration, an installed skill, a live DE; `provisioning_setting.feature_rules`). Only `rule`
 * rows are ever switched off again by the rules; any other source (a `manual` one above all) is
 * an admin's override the rules leave alone.
 */
export const entitlementSource = ["plan", "addon", "trial", "manual", "bundled", "rule"] as const;

// cell: identity and org
/**
 * FOUNDER-OS (12; ADR-0013): `portfolio` is an Account's Founder OS company (exactly one per
 * account, created with it); every other company is `operating`.
 */
export const companyKind = ["operating", "portfolio"] as const;
/**
 * M4-01 (02 §membership): `personal_agent` is a person's crew, one per person membership.
 * SYSTEM-AGENT (founder, 2026-10-29): `system_agent` is the platform's own agent in a company
 * (Skillsmith): no seat, no KPIs, nobody in the company sees it or can address it; it reads the
 * company's runs, verdicts, corrections and skill usage and writes feedback only to the
 * operator-only `system_agent_feedback`.
 */
export const membershipKind = [
  "person",
  "digital_employee",
  "personal_agent",
  "system_agent",
] as const;
/** SYSTEM-AGENT: what a system agent's feedback report is about. */
export const systemAgentFeedbackKind = [
  "failure",
  "verdict",
  "correction",
  "skill",
  "improvement",
  "other",
] as const;
/**
 * SKILLSMITH-HUB (founder, 2026-10-30): how urgent a report is, most urgent first. The criteria
 * (Skillsmith's instructions and the hub's (i) popover, `SKILLSMITH_SEVERITY_CRITERIA`):
 * critical — client-facing harm, a data or security risk, money, or work stopped;
 * high — repeated failures or a KPI at risk; medium — quality issues or a skill gap;
 * low — polish or ideas. (0183 mapped the old problem/warn/info to high/medium/low.)
 */
export const systemAgentFeedbackSeverity = ["critical", "high", "medium", "low"] as const;
/**
 * SKILLSMITH-HUB: where a report's suggested fix stands. `proposed`: waits for a super admin;
 * `applied`: a super admin approved it (a structured change was written, audited); `rejected`;
 * `reverted`: an applied change was undone (audited). `none`: the report suggests nothing.
 */
export const systemAgentFixStatus = [
  "none",
  "proposed",
  "applied",
  "rejected",
  "reverted",
] as const;
/** SKILLSMITH-HUB: what an operator inbox item is (a Critical report; a paused system agent). */
export const operatorInboxKind = [
  "report",
  "paused",
  "memory_quality",
  // ONBOARDING-P2: a client's onboarding follow-ups ran out without a complete intake.
  "onboarding_alert",
  // ONBOARDING-P3: an approved DE seat had no fitting template (a template ticket was opened).
  "template_ticket",
  // ONBOARDING-P4: a client's message to its system address (routed to the operator, or held in
  // quarantine), a 30-day review ready for the operator, a DE the operator provisions by hand.
  "client_message",
  "onboarding_review",
  "de_provision",
] as const;
/** SKILLSMITH-HUB: who filed an "Agent issues" entry (an agent, or the platform itself). */
export const agentIssueSource = ["agent", "system", "person"] as const;
/**
 * SKILLSMITH-HUB: what an "Agent issues" entry is. Agents file the first five (MCP
 * `report_issue`); the platform files the hard failures itself.
 */
export const agentIssueKind = [
  "tool_error",
  "blocker",
  "confusing_instruction",
  "repeated_failure",
  "human_correction",
  "run_failed",
  "worker_failed",
  "verdict_blocked_loop",
  "timeout",
  // SKILL-OVERRIDES: a manager asked to change a skill that lives in a self-hosted runtime
  // (filed by a person, `source = person`).
  "skill_change",
  // HANDOFF-NO-DROPS: a person's request stopped moving (a helper card blocked, a run stopped,
  // past its expected time, a wake or a delivery refused); the requester was told in plain words.
  "chain_stalled",
] as const;
/** SKILLSMITH-HUB: where an "Agent issues" entry stands (the system agent moves it). */
export const agentIssueStatus = [
  "new",
  "investigating",
  "reported",
  "resolved",
  "duplicate",
  "wont_fix",
] as const;
/**
 * SKILLSMITH-OPPORTUNITIES (founder, 2026-10-30): a sales opportunity in the Agent health hub —
 * a helper (an AG under a DE) or a skill that would fix what a DE keeps getting wrong. Where it
 * came from: Skillsmith's sweep (issue log + aggregate counts), or the client asked for a helper.
 */
export const opportunitySource = ["skillsmith", "client_asked"] as const;
/**
 * SKILLSMITH-OPPORTUNITIES: the pipeline. suggested → offered (an offer email or a quote sent) →
 * accepted | declined → added (the helper delivered); dismissed by a super admin at any open step.
 */
export const opportunityStage = [
  "suggested",
  "offered",
  "accepted",
  "declined",
  "added",
  "dismissed",
] as const;
/** SKILLSMITH-OPPORTUNITIES: what it suggests building. */
export const opportunitySuggestionKind = ["helper", "skill"] as const;
/** SKILLSMITH-OPPORTUNITIES: how sure Skillsmith is that it would fix the problem. */
export const opportunityConfidence = ["high", "medium", "low"] as const;
/**
 * SKILLSMITH-OPPORTUNITIES (founder, 2026-10-30): a paid helper quote the platform sends a
 * company. open (waits for the client's owner or admin) → approved: charged, its monthly line
 * added, the build starts (`building`) → `added` once delivered; or declined (by the client) /
 * canceled (by us).
 */
export const helperOfferStatus = ["open", "declined", "canceled", "building", "added"] as const;
/**
 * SKILLSMITH-OPPORTUNITIES: the one-off charge of an approved quote. `none`: nothing to charge;
 * `recorded`: the invoice is recorded and the charge waits for the one-time charge path
 * (billing phase 3, flags.ts `ONE_TIME_CHARGES` — a stub until it ships); `queued`: handed to
 * the billing operations; `charged`.
 */
export const helperChargeStatus = ["none", "recorded", "queued", "charged"] as const;
/**
 * ADMIN-INSIGHTS (design 2026-10-30-admin-insights-agent.md §4): the measures of the metrics
 * catalogue. Flow measures (spend, tasks done or created) have timestamps; state measures (open,
 * in progress, overdue, approvals waiting) get a `metric_snapshot` row per day for their history.
 */
export const insightMeasure = [
  "agent_spend",
  "budget_use",
  "tasks_done",
  "tasks_created",
  "tasks_open",
  "tasks_in_progress",
  "tasks_overdue",
  "approvals_waiting",
] as const;
/** ADMIN-INSIGHTS: how a measure is broken down. */
export const insightBy = ["none", "agent", "assignee_kind", "assignee", "day", "week"] as const;
/** ADMIN-INSIGHTS: the period a flow measure covers, in company time. */
export const insightPeriod = [
  "today",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "last_30_days",
  "this_quarter",
] as const;
/** ADMIN-INSIGHTS: how a pinned answer is drawn on Home. */
export const insightViz = ["number", "bars", "line", "table"] as const;
/** ADMIN-INSIGHTS: who sees a pinned answer on Home: its owner, or every owner and admin. */
export const insightVisibility = ["me", "admins"] as const;
/** ADMIN-INSIGHTS: how a question was mapped to the catalogue (rules first, the model second). */
export const insightAskVia = ["rules", "model", "none"] as const;
/** SYSTEM-AGENT: who wrote in a system agent's private space (a platform super admin, or it). */
export const systemAgentMessageAuthor = ["super_admin", "system_agent"] as const;
/**
 * SYSTEM-AGENT: what a message in that space is — a plain message, an investigation the agent
 * opens, or its report back on one.
 */
export const systemAgentMessageKind = ["message", "investigation", "report"] as const;
/**
 * SYSTEM-AGENT: what a system agent's own runtime key (`cosk_sa_`) may do — read the company's
 * signals, file reports, answer in the super admin's space (which it reads to answer). Nothing else.
 */
export const systemAgentKeyScope = ["signals:read", "feedback:write", "space:write"] as const;
export const systemAgentKeyStatus = ["active", "revoked"] as const;
/**
 * Who may hold a seat: never a crew (02 §Personal-crew invariants). SYSTEM-AGENT: `system_agent`
 * only on a closed assignment — the history of a DE converted into a system agent (the kind
 * cascades from `membership`; `seat_assignment_system_agent_closed_check`).
 */
export const seatHolderKind = ["person", "digital_employee", "system_agent"] as const;
export const membershipStatus = ["active", "paused", "deactivated"] as const;
/** A pending membership (S1-06 review: no access until the invitee proves the mailbox). */
export const invitationStatus = ["pending", "accepted", "revoked"] as const;
/** The company relations an invitation may carry (granted on acceptance). */
export const invitationRole = ["owner", "admin", "member", "client_viewer"] as const;
export const seatKind = ["standard"] as const;
export const seatStatus = ["proposed", "open", "filled", "paused", "closed"] as const;
export const seatOrigin = ["manual", "template", "assessment", "import"] as const;
/**
 * DE-SEAT-OWNER (behind `settings.flags.de_seat_owner`): what a DE on the seat becomes when no
 * person holds it — `assist` stays an assistant, `act_when_vacant` acts as owner while the seat
 * has no person, `own` owns it with no person expected. Null: never set (the behaviour before).
 */
export const seatDeFill = ["assist", "act_when_vacant", "own"] as const;
/** DE-SEAT-OWNER: a DE's assignment's meaning (null on a person's, and before the flag). */
export const seatAssignmentCapacity = ["assistant", "acting", "owner"] as const;
/**
 * DE-FOLLOWUPS: `not_deployed` — on the chart, but no runtime yet (never ran; not a pause).
 * It runs nothing and its keys are unbound, like `paused`.
 */
export const digitalEmployeeStatus = [
  "onboarding",
  "active",
  "paused",
  "retired",
  "not_deployed",
] as const;
/** WORKFORCE (2026-10-26): an agent (AG), a task executor with no seat, KPIs or budget. */
export const workforceAgentStatus = ["active", "paused", "retired", "not_deployed"] as const;
/** Whom an agent reports to: a digital employee or a person (never a crew). */
export const workforceAgentReportsToKind = ["person", "digital_employee"] as const;
/**
 * M1-02 (02 §agent_key): `agent` keys belong to a DE (or crew) principal and one seat; `service`
 * keys (Recall, 04 §Auth model) belong to one principal and carry no seat.
 */
export const agentKeyKind = ["ingest", "agent", "service"] as const;
export const agentKeyStatus = ["active", "revoked"] as const;
/** The scopes an ingest key may hold (13 §Ingest); every other scope is an agent/service scope. */
export const ingestKeyScope = [
  "ingest:activity",
  "ingest:outcome",
  "ingest:cost",
  // MONITORING-P1: run and job telemetry a DE's bridge ships (`telemetry` items).
  "ingest:telemetry",
] as const;
/**
 * M3-04 (D-16): a source-scoped ingest key posts `time` items of one time source (the internal
 * channel's rules over HTTP) and holds exactly this scope; a DE's ingest key never holds it.
 */
export const sourceKeyScope = ["ingest:time"] as const;
/** The time sources a source-scoped key may post for (`time_entry.source`, never `manual`). */
export const ingestKeySource = ["workpulse", "tracker", "timer", "import"] as const;
/** M3-04 (D-15): how an operator settled a stuck meter identity. */
export const meterSettlementResolution = ["repost", "reconcile"] as const;
/** 04 §Auth model: `{resource}:{read|write|*}` over these resources. */
const scopedResources = [
  "tasks",
  "boards",
  "messages",
  "channels",
  "pages",
  "approvals",
  "runs",
  "events",
  "kpis",
  "time",
  "reminders",
  "goals",
  "promotions",
  /** TASK-FILES: task files (list, read, upload, rename, trash). */
  "files",
] as const;
type ScopedResource = (typeof scopedResources)[number];
const agentScopes = scopedResources.flatMap(
  (r) => [`${r}:read`, `${r}:write`, `${r}:*`] as const,
) as `${ScopedResource}:${"read" | "write" | "*"}`[];
/**
 * Every key scope: the ingest scopes, then the agent/service scopes of 04 (plus `seats:read`,
 * `people:read`). DE keys can never hold `seats:write`, `people:write`, `agent_keys:*` or
 * `access:*` (05 §Agent scopes): those are not scopes at all.
 */
export const agentKeyScope = [
  ...ingestKeyScope,
  ...sourceKeyScope,
  ...agentScopes,
  "seats:read",
  "people:read",
  /** ADR-0018 §6: the Deployment engineer's agent key stages deploys (control plane only). */
  "deploys:stage",
] as const;
export const externalIdentityKind = ["person", "seat", "de", "client"] as const;
/** M1-02 (02 §team): derived from its seats' assignments. */
export const teamComposition = ["human", "mixed", "digital"] as const;
/** M1-02 (02 §seat_edge): the org chart's three edge kinds. */
export const seatEdgeKind = ["reports_to", "authority", "delegation"] as const;
export const responsibilityStatus = ["active", "archived"] as const;
export const recurringWorkStatus = ["active", "paused"] as const;
/**
 * CHART-KB (02 §goal, 06 §Quarterly priorities, 01 "Goal"): a goal's kind — `priority` is the
 * cadence module's quarterly goal (a seat's keystone on the chart) — its state and its track
 * (on / off / at risk, set by the owner until M5's kpi worker derives it).
 */
export const goalKind = ["goal", "priority", "annual"] as const;
export const goalState = ["open", "achieved", "missed", "dropped"] as const;
export const goalTrack = ["on", "off", "at_risk"] as const;
/**
 * MEETINGS (02 §Operating cadence; 06 §Cadence): an issue's status (identify → discuss → solve,
 * then resolved or dropped), what its resolution made, and how far up it was escalated; what a
 * cadence item came from.
 */
export const issueStatus = ["identify", "discuss", "solve", "resolved", "dropped"] as const;
export const issueResolutionKind = ["task", "decision", "none"] as const;
export const issueEscalation = ["team", "company", "portfolio"] as const;
export const issueSourceKind = [
  "manual",
  "message",
  "task",
  "metric",
  "goal",
  "meeting",
  // BIZ-CLIENT-HEALTH: about a client (`source_id` the client's space): a detractor's answer, a
  // health score turning red, or raised from the client's health card
  "client",
] as const;
/**
 * M8-ASSESS (02 §Assessment; x-ray.md §11.2): an assessment round's questionnaire (`full` team
 * X-Ray or the `delta` quick scan) and life (draft → open → closed → reported); a respondent is a
 * company member or an invited `guest` (no membership, a magic link only) and moves invited →
 * opened → in_progress → completed (or expired); a proposal is suggested, accepted or rejected by
 * the owner, then applied.
 */
export const assessmentQuestionnaire = ["full", "delta"] as const;
export const assessmentRoundState = ["draft", "open", "closed", "reported"] as const;
export const assessmentParticipantKind = ["member", "guest"] as const;
export const assessmentParticipantState = [
  "invited",
  "opened",
  "in_progress",
  "completed",
  "expired",
] as const;
export const assessmentProposalKind = ["seat", "responsibility", "metric"] as const;
export const assessmentProposalState = ["proposed", "accepted", "rejected", "applied"] as const;
/** 02 `meeting_series.kind`, `meeting.state` (MEETINGS: scheduled → live → ended, or cancelled). */
export const meetingSeriesKind = ["weekly", "quarterly", "annual", "portfolio"] as const;
export const meetingState = ["scheduled", "live", "ended", "cancelled"] as const;
/**
 * MEETINGS: the pre-read of a meeting: none asked yet, a wake-up queued for the team's agent,
 * `pending` when no agent of the team is live to prepare it, `ready` once written.
 */
export const meetingPrereadState = ["none", "requested", "pending", "ready"] as const;
/** 02 `meeting_agenda_item.section`, its source and its outcome. */
export const agendaSection = [
  "scorecard",
  "priorities",
  "issues",
  "todos",
  "headlines",
  "custom",
] as const;
export const agendaSourceKind = ["metric", "goal", "issue", "task", "none"] as const;
export const agendaOutcomeKind = ["task", "decision", "issue", "none"] as const;
/** MEETINGS: an outcome captured in a meeting (`meeting_outcome.kind`). */
export const meetingOutcomeKind = ["task", "decision", "issue_resolved", "issue_raised"] as const;
/**
 * CALENDAR-C1 (design 2026-10-28-calendar §2.1): a calendar is the company's (one, made with the
 * company), a team's (made on first use) or a person's (made with the membership).
 * CAL-WORK-LAYERS (design 2026-10-30-calendars-and-meetings §C.1): two layer kinds that hold no
 * events: `work`, a board's calendar view published into the Calendar (its tasks on the view's
 * date), and `milestones`, the company's dated goals, priorities and KPI targets (one per
 * company). Neither counts as busy, feeds Find a time or leaves the product (ICS, sync).
 */
export const calendarKind = ["company", "team", "person", "work", "milestones"] as const;
/** What others see of a calendar by default: everything, a busy block, or nothing. */
export const calendarVisibility = ["details", "busy", "hidden"] as const;
/** `calendar_event.status`; a deleted event is `cancelled` (kept 30 days as a tombstone). */
export const calendarEventStatus = ["confirmed", "tentative", "cancelled"] as const;
/** `calendar_event.visibility`: `default` takes the calendar's; the rest override it. */
export const calendarEventVisibility = ["default", "details", "busy", "hidden"] as const;
/** Where an event came from: a person, a cadence meeting's projection, a suggestion card, a sync. */
export const calendarEventSource = ["manual", "meeting", "suggestion", "external"] as const;
export const calendarAttendeeResponse = [
  "needs_action",
  "accepted",
  "declined",
  "tentative",
] as const;
/** `calendar_share.level`: busy blocks, details, or details and edits. */
export const calendarShareLevel = ["busy", "read", "write"] as const;
/** CAL-WORK-LAYERS: `seat` reaches whoever holds the seat now (a DE is a `membership`). */
export const calendarShareGrantee = ["membership", "team", "seat", "company"] as const;
/** `calendar_event_revision.reason` (sync reasons arrive with C2). */
export const calendarRevisionReason = ["edit", "sync_conflict", "sync_pull"] as const;
/** WOS-BUNDLE: `paused` after 5 events in a row exhausted their retries (Work OS auto-pause). */
export const webhookStatus = ["active", "disabled", "paused"] as const;
export const webhookDeliveryStatus = ["pending", "succeeded", "failed", "abandoned"] as const;
/**
 * WEBHOOKS-IN (design 2026-10-27 §2): how an incoming webhook proves a delivery is its sender's.
 * Signed modes only; the unsigned `token` mode of the design is not built (§8.3 held back).
 */
export const inboundWebhookVerification = ["companyos", "stripe", "github"] as const;
/** How a logged delivery came in: a sender's (its hook's mode), the dialog's sample, a replay. */
export const inboundDeliveryVerification = [
  ...inboundWebhookVerification,
  "sample",
  "replay",
] as const;
/** `paused`: senders get 503 and retry later; `disabled`: 410, they stop. */
export const inboundWebhookStatus = ["active", "paused", "disabled"] as const;
/** `company`: the company's content-screen setting; `enforce`: this hook withholds flagged data. */
export const inboundWebhookScreenMode = ["company", "enforce"] as const;
/**
 * `inbound_delivery.status`: `accepted` holds the body until the purge; the refusals never held
 * one; `dropped` verified but made no task (the target DE holds no seat, or no live board).
 */
export const inboundDeliveryStatus = [
  "accepted",
  "rejected",
  "rate_limited",
  "paused",
  "dropped",
] as const;
/** cp `account_grant.level` (12 §Data model additions). */
export const accountGrantLevel = ["owner", "portfolio_member", "portfolio_viewer"] as const;
/**
 * FOUNDER-OS: how a company sits in an account's portfolio — `owned` (the account holder owns
 * it) or `client` (overseen through the holder's agency operator grant, "Clients via HTVA").
 */
export const accountCompanyRelation = ["owned", "client"] as const;
/** FOUNDER-OS: the founder brief's cadence and who wrote it (the system, or the founder crew). */
export const founderBriefCadence = ["daily", "weekly"] as const;
export const founderBriefSource = ["system", "agent"] as const;

// cell: spaces, chat, inbox (M2-01; 02 §Spaces, channels, messages; 06)
export const spaceKind = ["team", "project", "client", "personal"] as const;
export const channelKind = ["space", "team", "board_home", "dm", "group_dm", "agent_room"] as const;
export const channelMemberRole = ["admin", "member"] as const;
/** M2-02: who may post in a channel beyond its viewers' poster rule (`#announcements`: admins). */
export const channelPostingPolicy = ["everyone", "admins"] as const;
/**
 * CHAT-THREAD-AGENTS: why an agent takes part in a reply thread (`thread_read.participant`): it
 * started it (wrote the root, or the root named it), it is the root's target (a task card's
 * assignee, a DM's agent), it posted a reply, or a reply mentioned it.
 */
export const threadParticipantRole = ["started", "target", "posted", "mentioned"] as const;
/** M2-02: a member's notification level for one channel (06; Zulip stream settings). */
export const channelNotifyLevel = ["all", "mentions", "none"] as const;
/**
 * M2-04 (M2-06's contract): when a channel's agents wake. `mentions` (default): a mention, an
 * assignment or a reply in a thread the agent started; `topic`: every message in a topic the
 * agent is in; `silent`: only on schedule.
 */
export const agentWakeRule = ["mentions", "topic", "silent"] as const;
/**
 * DE-INITIATE: what a channel lets its DEs start unprompted (it can only narrow the company's
 * rules): `allowed` (default), `routines` (declared routines only), `off` (nothing unprompted).
 */
export const agentInitiateRule = ["allowed", "routines", "off"] as const;
/** DE-INITIATE: what an unprompted post must be about (its link chip). */
export const deInitiationAboutKind = ["task", "kpi", "deadline", "approval"] as const;
/** DE-INITIATE: where it goes: a new thread in a channel, or a DM with a person. */
export const deInitiationTarget = ["channel", "dm"] as const;
/**
 * DE-INITIATE: `posted` (a new thread or DM post), `collapsed` (a reply in its own unprompted
 * thread of the last hour), `queued` (quiet hours; `sent` or `failed` when the job delivers it),
 * `refused` (the audit record of a refusal, with its code).
 */
export const deInitiationOutcome = [
  "posted",
  "collapsed",
  "queued",
  "sent",
  "failed",
  "refused",
] as const;
/** DE-INITIATE: why a start was refused (the tool's refusal names it in plain words). */
export const deInitiationRefusal = [
  "company_off",
  "de_off",
  "not_self_hosted",
  "not_a_member",
  "channel_off",
  "channel_routines_only",
  "agent_room",
  "about_not_found",
  "out_of_scope",
  "dm_not_allowed",
  "rate_limited",
  "routine_not_declared",
  "routine_done",
  "one_home",
  // Codex r1 #7: a collapse would land in quiet hours.
  "quiet_hours",
  // Codex r1 #15: the post itself was refused (the topic policy, the thread's rules).
  "cannot_post",
] as const;
/**
 * DE-BRIEFS: whose brief settings a row holds (the company's defaults, a person's for the briefs
 * they receive, a DE's as its manager set them), the rhythm, what was sent, and what became of it.
 */
export const briefScope = ["company", "person", "de"] as const;
/** `daily`: Monday to Friday; `weekly`: one chosen day; `custom`: a chosen set of days. */
export const briefFrequency = ["daily", "weekly", "custom"] as const;
export const deBriefKind = ["brief", "cos_report"] as const;
/** COS-BRIEF-RULES: `sending` — an email report committed, sent after the job's transaction. */
export const deBriefState = ["drafting", "queued", "sending", "sent", "skipped", "failed"] as const;
/**
 * COS-BRIEF-RULES: who a brief rule sends the Chief of Staff's report to (the company owner, a
 * role resolved when it is sent, or a person), which of its sections, and how it is delivered.
 */
export const briefRuleRecipient = ["owner", "person"] as const;
export const briefSection = ["work", "kpis", "stuck", "decisions"] as const;
export const briefChannel = ["inbox", "dm", "email"] as const;
/** What a reply to a brief became: a comment on the item it named, or a note in the DM only. */
export const deBriefCorrectionApplied = ["task_comment", "dm_only"] as const;
/** M2-04: what `GET search` finds (06 §Shell "quick-switcher search"). */
export const searchHitKind = ["message", "task"] as const;
/** M2-02: who wrote a message, from the author membership's kind (null author: system). */
export const messageAuthorKind = ["person", "agent", "system"] as const;
export const topicStatus = ["open", "resolved"] as const;
/**
 * CHAT-TOPICS-SEPARATE: how a topic member came to be one — it opened the topic, someone added
 * it, it joined (an open topic, one click), it posted there, it was @mentioned there, or 0105's
 * backfill (past posters and mentioned people).
 */
export const topicMemberVia = [
  "creator",
  "added",
  "joined",
  "posted",
  "mentioned",
  "backfill",
] as const;
export const threadKind = ["topic", "task", "approval", "page", "run"] as const;
/**
 * CHAT-FILES: `upload` is a chat file staged by its uploader before its message exists
 * (`subject_id` = the uploader's membership, `state` pending); sending binds it to the message.
 */
export const attachmentSubjectKind = ["message", "task", "page", "upload"] as const;
/** M2-05: `pending` until the upload is confirmed, `quarantined` when the scan flags it. */
export const attachmentState = ["pending", "ready", "quarantined"] as const;
/**
 * KB-PDF-UPLOAD: an uploaded document's text extraction: `processing` while it is read, `done`
 * (its Markdown is the page's body), `no_text` (no text layer: a scan; no OCR), `failed` (retry).
 */
export const pageUploadStatus = ["processing", "done", "no_text", "failed"] as const;
export const followSubjectKind = ["channel", "topic", "thread", "task", "board"] as const;
export const followLevel = ["all", "mentions"] as const;
/** 06 §Inbox. */
export const inboxItemKind = [
  "mention",
  "reply",
  "task_change",
  "assignment",
  "approval",
  "reminder",
  "budget_alert",
  "agent_escalation",
  "system",
  // M7-03: a Recall proposal, decision outcome or contradiction for a scope's reviewer (07)
  "memory",
  // SEARCH-NOTIF: a digital employee's run finished or failed (its supervisor seat's holders),
  // and a scorecard number that is due or late (06 §Inbox, notification preferences).
  "agent_result",
  "kpi_due",
  // MEMBER-ENTRY: a seat holder corrected a past period's number (its supervisor is told)
  "kpi_correction",
  // COS-MODES: an agent directed one of the person's agents (Team mode, Tell me); Take back
  "agent_directed",
] as const;
/**
 * SEARCH-NOTIF (06 §Notification preferences): how often the inbox email digest is sent;
 * `off` sends none.
 */
export const digestFrequency = ["off", "hourly", "daily", "weekly"] as const;
/**
 * INBOX-PREFS (founder-approved 2026-10-29): the notification types a person routes (the
 * settings grid's rows; an inbox item's `category`). `waiting` is never fully off.
 */
export const notificationCategory = [
  "waiting",
  "mentions",
  "dms",
  "tasks",
  "de_activity",
  "kpis",
  "reports",
  "meetings",
  "followed",
] as const;
/** INBOX-PREFS: the presets a person starts from (an admin sets the company's default). */
export const notificationPreset = ["everything", "needs_me", "manager", "quiet"] as const;
/** DE-REPLY-STYLE: how long a person wants DEs' chat replies (no choice: `short`). */
export const replyLength = ["short", "normal", "detailed"] as const;
/** INBOX-PREFS: a per-item override on a task or a DE (channels keep their own level). */
export const notificationOverrideLevel = ["all", "mentions", "mute"] as const;
export const inboxSubjectKind = [
  "message",
  "topic",
  "task",
  "approval",
  "budget",
  "webhook",
  // WEBHOOKS-IN: an incoming hook that paused itself (rejected signatures)
  "inbound_webhook",
  "delegation_request",
  "run",
  // M7-03 (07 §Webhooks)
  "promotion",
  "decision",
  "reminder",
  "recall_notice",
  // M5-05: an agency user's break-glass request, start and end (05 "a banner shows in the company
  // for all admins"; 03 `access.break_glass_*` consumers "inbox (company admins)")
  "break_glass_session",
  // SEARCH-NOTIF: a seat's scorecard number that is due or late (`kpi_due`)
  "seat",
  // MEMBER-ENTRY: the corrected observation row
  "observation",
  // AGENT-REPORTING: a reporting duty's reminder or late notice (13 §Reporting duties)
  "reporting_duty",
  // HARDEN: an operations signal (database, queue lag, dead letters, disk) crossed its threshold
  "ops_signal",
  // BILLING-PLANS: payment failed / overdue, the reminder, suspension and restore (owners, admins)
  "company_subscription",
  // BILLING-PLANS: a client company's billing change that failed for good (the agency's
  // operators, in HTVA's own company; the subject id is the client company)
  "billing_company",
  // Parity P5: an automation that paused itself or refused effects (its owner's notice), and an
  // automation's brief to the agent it wakes
  "automation",
  // FOUNDER-OS: a portfolio alert about one company of the account (the Founder OS inbox)
  "portfolio_company",
  // WALLET-P1: the company's credit is low or used up (owners and admins; opens Billing)
  "wallet",
  // REACH-S1: a person asks to talk to an agent outside its reach (the serving person grants inline)
  "reach_request",
  // COS-MODES: the Chief of Staff's mode (the CEO's "switch to Team?"; opens its Setup page; the
  // subject id is the company)
  "chief_of_staff",
  // STEP1-OUTBOX: an outward act became a dead letter (the connection's owner, else the
  // company's owners and admins; opens the Outbox)
  "action_outbox",
  // INC2-INTAKE: an integration job became a dead letter (the connection's owner, else the
  // company's owners and admins; opens Intake › Dead letters)
  "integration_job",
  // MANAGED-POLICY: a request for an agent (its agency's operators; the requester on resolution)
  "agent_request",
  // STRUCTURE-CHANGED: a reporting line moved (both leaders' notice; the new one's re-route list)
  "structure_change",
  // COS-BRIEF-RULES: a Chief of Staff report a brief rule delivers to the inbox (the recipient)
  "de_brief",
  // SKILLSMITH-OPPORTUNITIES: a helper quote the platform sent (owners and admins; opens Billing,
  // Approve / Decline)
  "helper_offer",
  // MODEL-KEYS-PLANS: the company's plan stopped allowing its own model keys (owners and admins;
  // opens Connections › API keys; the subject id is the company)
  "model_access",
  // UPLOAD-PRIVATE-FIRST: an uploaded doc's text was read (a large one) or could not be read
  // (its uploader; opens the doc)
  "page",
  // CLOSED-LOOPS: a commitment asked of the person or by them — a reminder, an escalation up the
  // reporting chain, a result to confirm, or a failure's options to choose from (opens its card)
  "commitment",
  // lane B (REQUEST-RECOVERY, 0245): an interrupted request's one recovery item (Continue / Take
  // over / Dismiss; the accountable person)
  "recovery",
] as const;

// ── STRUCTURE-CHANGED (docs/design/2026-10-30-shared-des-and-fulfillment.md §3.2) ──
/** `structure_change.kind`: a reports-to line (seat parent or AG reports-to), serves, a holder. */
export const structureChangeKind = ["reports_to", "serves", "holder"] as const;
/** `structure_change.subject_kind`: a seat (its parent or serves) or an AG (its reports-to). */
export const structureSubjectKind = ["seat", "workforce_agent"] as const;
/** `structure_note.agent_kind`: who is told — a DE (its membership) or an AG. */
export const structureAgentKind = ["digital_employee", "workforce_agent"] as const;

// cell: boards and tasks (M2-01; 02 §Boards and tasks; 06)
export const boardDigestInterval = ["off", "hourly", "daily", "weekly"] as const;
/** BOARD-ORDER: a board's colour, one of the design system's six tints (`--avatar-1..6`). */
export const boardColor = ["orange", "green", "purple", "amber", "blue", "rose"] as const;
export const fieldType = [
  "text",
  "number",
  "select",
  "multi_select",
  "seat",
  "date",
  "checkbox",
  "url",
  "currency",
  "formula",
  "relation",
  "rollup",
] as const;
export const fieldAudience = ["human", "agent", "both"] as const;
export const viewKind = ["board", "list", "table", "calendar", "timeline", "gallery"] as const;
export const taskOrigin = [
  "manual",
  "recurring",
  "message",
  "agent",
  "recall_commitment",
  "template",
  "import",
  // DC-10: a failed drift audit opens a task on the DE's supervisor seat (migration 0027).
  "drift_audit",
  // MEETINGS: a to-do captured in a meeting, or a task an issue's resolution created (06 §Cadence
  // "To-dos": tasks with a 7-day default due, created from meetings/issues; migration 0058).
  "meeting",
  "issue",
  // WOS-BUNDLE: a copy of another task (Work OS `duplicateTask`; `origin_ref.task_id`).
  "duplicate",
  // WEBHOOKS-IN: a hook rule's task (`origin_ref.inbound_webhook_id` / `inbound_delivery_id`).
  "webhook",
  // INC2-INTAKE: the one intake's task for a handler DE (`origin_ref.inbound_event_id`).
  "intake",
] as const;
export const taskDependencyKind = ["hard", "soft"] as const;
/**
 * WOS-BUNDLE: a tag's colour (Work OS `TAG_COLORS` plus its neutral `zinc` default; a tag made
 * without one takes `TAG_COLORS[|hash(name)| % 8]`).
 */
export const tagColor = [
  "zinc",
  "violet",
  "sky",
  "emerald",
  "amber",
  "rose",
  "indigo",
  "teal",
  "orange",
] as const;
/**
 * WOS-BUNDLE: a stored task link. Work OS has one symmetric "related" link; CompanyOS adds the
 * directed "duplicates" (`task_id` duplicates `linked_task_id`). "Blocks" is a hard dependency
 * (`task_dependency`), shown with the links but stored there.
 */
export const taskLinkKind = ["relates_to", "duplicates"] as const;
/** WOS-BUNDLE: how a link reads from one task (the picker's choices and the drawer's groups). */
export const taskLinkRelation = [
  "relates_to",
  "duplicates",
  "duplicated_by",
  "blocks",
  "blocked_by",
] as const;
/** WOS-BUNDLE: what a person may star (Work OS starred containers; CompanyOS widens it). */
export const favoriteKind = ["space", "board", "page", "task", "channel"] as const;
/** WOS-BUNDLE: what a person's recents remember (Work OS `RecentKind`: container, task, doc). */
export const recentKind = ["space", "board", "page", "task"] as const;
/**
 * WOS-BUNDLE: My Tasks' built-in sections (Work OS `BUILTIN_BUCKETS`: recent, today, next_week,
 * later; "next week" reads "Upcoming" here). A new assignment lands in `recent`.
 */
export const myTaskBuiltinSection = ["recent", "today", "upcoming", "later"] as const;

// cell: pages (M7-02; 02 §Knowledge, + the cadence/founder kinds of the program's M7-02 row)
export const pageKind = [
  "page",
  "sop",
  "policy",
  "kb",
  "task_body",
  "vision",
  "journal",
  "decision",
  "strategy",
  // T6-BOARD-NOTE: a board's "How this board works" note (`board.page_id`), written by its owner
  "board_note",
] as const;
/** DOCS-KB: what an agent's knowledge source points at: one page, a whole space, or every page of a kind. */
export const knowledgeSourceKind = ["page", "space", "kind"] as const;
/**
 * BIZ-PLAYBOOKS: what a playbook link says about an SOP page (`playbook_link.kind`): the seat
 * that `owner`s it (approves its changes), a seat that `follow`s it (its holder, person or DE,
 * runs it), or an item `template` a run of it starts from (its checklist is the playbook's steps).
 */
export const playbookLinkKind = ["owner", "follower", "template"] as const;
/** BIZ-CLIENT-HEALTH: a client survey's question: NPS (0–10) or CSAT (1–5). */
export const clientSurveyKind = ["nps", "csat"] as const;
/** BIZ-CLIENT-HEALTH: a client's health band (`client_health_snapshot.band`). */
export const clientHealthBand = ["green", "amber", "red", "none"] as const;

// agents, runs, approvals (M3-01; 02 §Identity (agent rows), §Approvals, runs, cost; ADR-0021)
/** cp `agent_template.scope`: platform (HTVA catalog) or one agency's own. */
export const agentTemplateScope = ["platform", "agency"] as const;
export const agentTemplateKind = ["persistent", "ephemeral"] as const;
export const agentTemplateStatus = ["draft", "published", "deprecated"] as const;
/**
 * Skill packs (M4-02; 02 §Agents and crews `skill_pack`, review fix #7): the cp catalog holds
 * `platform` and `agency` packs, the cell copy adds the company's own (`company`). A published
 * pack is immutable; a deprecated one stays readable and is no longer mounted.
 */
export const skillPackScope = ["platform", "agency", "company"] as const;
export const skillPackStatus = ["published", "deprecated"] as const;
export const skillPackFormat = ["herk-skill"] as const;
/**
 * SKILL-OVERRIDES: how a revision of a company's additions to a skill came about — an edit of
 * the text, a reset to the default (no additions), or a review of a new default (same text,
 * now read against the new default).
 */
export const skillAdditionVia = ["edit", "reset", "review"] as const;
export const agentProfileOwnerKind = ["digital_employee", "personal_crew"] as const;
export const agentProfileRole = ["lead", "specialist", "worker"] as const;
export const agentProfileState = ["active", "retired"] as const;
/** ADR-0021 §2 (supersedes 02's list): adds `drift_audit` and `pre_read`. */
export const wakeupKind = [
  "mention",
  "heartbeat",
  "inbox",
  "approval",
  "delegation",
  "manual",
  "drift_audit",
  "pre_read",
  // M4-05: an agent room hands an agent its turn; a status ask reaches its target agent.
  "room_turn",
  "status_ask",
  // AGENT-REPORTING: a reporting duty's "report your KPIs" wake-up (job `reporting.remind`)
  "reporting",
  // T2-BOARD-WAKE: the owning DE's batched look at what happened on its board.
  "board",
] as const;
/**
 * T2-BOARD-WAKE (universal-task plan §b.2): how a board event reaches the board's owning DE.
 * `now` wakes it at once (a person's comment, a person's move, an approval decided); `batch`
 * waits for the board's window. `never` (the DE's own writes, layout changes) is not journalled.
 */
export const boardEventClass = ["now", "batch"] as const;
/**
 * B5-DECIDE (universal-task plan §b.6): the band of one classifier decision. `act`: the top
 * option is at or above the rubric's act threshold (default 0.7); `review`: at or above its
 * review threshold (0.35), the DE reads the item itself; `unsure`: below, a person is asked.
 */
export const decisionBand = ["act", "review", "unsure"] as const;
/** B5-DECIDE: who classified: Jev on OpenRouter (the company's switch on) or the local light model. */
export const decisionProvider = ["jev", "local"] as const;
/** B5-DECIDE: what a classifier decision was for: a DE's `decide` call or the board-event gate. */
export const classifierDecisionSource = ["decide", "board_gate", "run_effort"] as const;
/**
 * RUN-EFFORT: how hard a run works. A caller asks for `low | medium | high` or `auto` (the system
 * picks); a run records the tier it got, where `vision` is the tier for work with images.
 */
export const runEffortRequest = ["auto", "low", "medium", "high"] as const;
export const runEffortTier = ["low", "medium", "high", "vision"] as const;
/** RUN-EFFORT: the reasoning a tier asks its model for (`off`: none). */
export const runEffortReasoning = ["off", "low", "medium", "high"] as const;
/** RUN-EFFORT: why a run got its tier (asked for, the agent's default, auto, the classifier). */
export const runEffortSource = ["requested", "default", "auto", "classifier"] as const;
/**
 * DC-08 (05 §Delegation rules 8): why admission refused an ephemeral worker (`run.worker_refused`).
 * The first three are the standing-orders gate: no deployed bundle to copy them from, a bundle
 * without them, or a context whose block is not the bundle's verbatim.
 */
export const workerRefusalReason = [
  "bundle_unavailable",
  "standing_orders_missing",
  "standing_orders_mismatch",
  "bundle_integrity_failed",
  "depth_exceeded",
  "budget",
  "invalid_request",
] as const;
/** ADR-0021 §2 (supersedes 02's `pending|coalesced|started|dropped`). */
export const wakeupStatus = ["queued", "coalesced", "dropped", "claimed", "done"] as const;
/**
 * MID-RUN-STEER (flag `de_mid_run_steer`): a person's message sent into its DE's running turn.
 * `pending` — taken out of the queue for the running run, the runtime's answer not in yet;
 * `delivered` — Hermes took it into the running turn; `fallback` — it could not (the run had just
 * ended, or the runtime refused) and went back to the queue for the next run.
 */
export const wakeupSteer = ["pending", "delivered", "fallback"] as const;
/**
 * PAUSE-NO-DROP: why a `queued` wake-up is held (null: it is not): its DE is paused (by a person
 * or its subscription), out of budget, or its wallet is empty. A held wake-up is never dropped;
 * it is released (the reason cleared, admission run again) when the DE is live again.
 */
export const wakeupHoldReason = [
  "paused",
  "budget",
  "wallet",
  // lane B (REQUEST-RECOVERY, 0245): the wake's request has an open recovery; released only by
  // its Continue, never by a DE resume
  "recovery",
] as const;
/** 02 §run: queued → running → waiting_* | awaiting_approval → completed | failed | cancelled. */
export const runState = [
  "queued",
  "running",
  "waiting_input",
  "awaiting_approval",
  "waiting_children",
  "completed",
  "failed",
  "cancelled",
] as const;
/**
 * AGENT-STABILITY: what became of a run lost to infrastructure (`runtime_lost`, `not_claimed`):
 * a successor is `scheduled` (after `retry_after`), `spawned` (`retried_by_run_id`), or the run is
 * `parked` for a person (attempts spent, side effects recorded, or admission refused).
 */
export const runRetryState = ["scheduled", "spawned", "parked"] as const;
export const runIdentityMode = ["as_agent", "on_behalf_of"] as const;
export const sandboxKind = ["browser", "computer"] as const;
export const runStepKind = ["tool_call", "model_call", "proposal", "note"] as const;
/** CANVAS-CORE: the Live canvas's ops (`show_user`, services/api/src/canvas/ops.ts). */
/**
 * CANVAS-BLOCKS added `focus_view` (the agent switches the person's Live view: Workspace,
 * Browser, Computer) and `interact` (a person's click on a block, read back by the agent).
 */
export const canvasOpVerb = [
  "show",
  "arrange",
  "highlight",
  "clear",
  "focus_view",
  "interact",
  // LIVE-DESKTOP: dock windows; windows' geometry on the desk.
  "tidy",
  "place",
] as const;
/**
 * CANVAS-CORE: what a canvas block shows (each rendered natively, under the viewer's rights).
 * CANVAS-BLOCKS added `image` and `video` (one file, through the authenticated file proxy).
 */
export const canvasBlockKind = [
  "board",
  "calendar",
  "doc",
  "image_grid",
  "kpi",
  "chain",
  "transcript",
  "image",
  "video",
] as const;
/** CANVAS-BLOCKS: Live's views (the page's tabs; `focus_view` and deep links name one). */
export const liveView = ["workspace", "browser", "computer"] as const;
/** CANVAS-BLOCKS: what a person did on a block (`interact`). */
export const canvasInteraction = [
  "open",
  "close",
  "move",
  "comment",
  "play",
  "pause",
  "view",
  "switch_view",
  // LIVE-DESKTOP: what the person did to a window.
  "moved",
  "resized",
  "docked",
  "restored",
  "closed",
  "pinned",
  "fronted",
] as const;
export const approvalKind = [
  "task_change",
  "message",
  "page",
  "external_action",
  "promotion",
  "delegation",
  "budget_overrun",
  // AGENT-REPORTING (13 §Reporting duties): an agent value on a `needs_review` metric
  "kpi_value",
  // Parity P6a (hybrid design §8.0 typed proposals): the payload of each is in
  // contracts/approvals.ts; executors land with the package that owns the effect
  "task_move",
  "task_delete",
  "board_config",
  "automation",
  "playbook_gate",
  "view_revision",
  "bulk",
  // D15 (preview): an agent's lesson, held for a person before any agent is briefed on it
  "memory_lesson",
  // AGENT-PLUMBING: a paid non-model service an agent reports (`report_cost`) above what it may
  // book by itself; approved, it is booked as reported by the agent
  "agent_cost",
  // CALENDAR-C1 (calendar design §6): an agent's create, update, cancel or RSVP on a calendar,
  // decided by the calendar's writers; the executor applies it as the deciding person
  "calendar_change",
  // T1-BOARD-MCP: an agent's `apply_template`, planned at filing and applied by the deciding
  // manager (contracts/approvals.ts `TemplateApplyPayload`)
  "template_apply",
  // T3-TASK-ROLLUPS: a DE's proposed task metric (a KPI counted from a board's tasks), created
  // and put on the board owner's seat when a company admin confirms it
  "task_metric",
  // T6-BOARD-NOTE: a DE's change to its board's note with the rule changes it describes, held as
  // one approval (the card shows both); applied as the deciding person
  "board_note",
  // B2-GATES: one review over N tasks of a board (keep / edit / send back / drop per row),
  // decided by the tasks' owner and applied row by row as that person
  "task_batch",
  // B2-GATES: an agent's (the Chief of Staff's) change to a person's gates on a board, from a
  // sentence; the person confirms it
  "gate_change",
  // BIZ-PLAYBOOKS: a DE's new version of a doc (a playbook: an SOP page), decided by the
  // playbook's owner seat, then the chain; applied as the deciding person
  "doc_update",
  // BIZ-CLIENT-HEALTH: a DE's NPS/CSAT survey to a client contact, held when its `external`
  // class asks; sent as the deciding person
  "client_survey",
  // RECALL-RULES: a standing rule a DE suggested (or a member asked for, or an agent filed in
  // Recall), decided by the company's owners first; approved, it binds and goes to Recall
  "memory_rule",
  // CORE-KEYS (core delivery plan item 1; migration 0254): an agent call the policy gate held on
  // an Ask (a seat, company or board row), filed by the gate itself, never by a caller. Approved,
  // it is a one-time permit: the agent's next identical call goes through (agent-policy/permits.ts)
  "agent_action",
  // CORE-AUTONOMY (core delivery plan item 6; migration 0261): the weekly autonomy review found
  // an agent meeting the promotion thresholds. Filed by the server only; approved, the agent's
  // level goes up one and the level's bundle is rendered into its seat rows (agent-policy/
  // autonomy-store.ts)
  "autonomy_change",
] as const;
/**
 * SKILL-PROPOSALS-OPERATOR (`skill_proposal.status`, an operator-only queue: no company sees it):
 * `proposed` waits for Skillsmith; `ready` Skillsmith attached its recommendation (and maybe a
 * revised draft) for the super admin; `approved` has a work item for the repo pipeline;
 * `rejected` was turned down; `shipped` the repo pipeline landed it.
 */
export const skillProposalStatus = [
  "proposed",
  "ready",
  "approved",
  "rejected",
  "shipped",
] as const;
/** SKILL-PROPOSALS-OPERATOR: what Skillsmith recommends the super admin do with a proposal. */
export const skillProposalRecommendation = ["approve", "reject"] as const;
/**
 * NIGHTLY-REVIEW (Andrei, 2026-11-01): who filed a proposal — the DE itself in a run
 * (`propose_skill`, `de`), or its nightly self-review (`nightly_review`: the platform reviewed the
 * DE's day and filed 0–3 proposals in its name). SKILLSMITH-LENS: `skillsmith`, a draft the
 * super admin filed from Skillsmith's findings (the same one door; nothing installed).
 */
export const skillProposalSource = ["de", "nightly_review", "skillsmith"] as const;
/**
 * NIGHTLY-REVIEW: the client conversation on a proposal (rule 7m: skill content is confidential,
 * so the client hears outcomes, never skill text). `needs_client_ok` the client owner has not
 * agreed yet (shipping is blocked); `client_ok` the operator recorded the client's yes;
 * `no_consent_needed` the operator recorded why none is needed (a bug fix, an internal-only DE).
 */
export const skillProposalConsent = ["needs_client_ok", "client_ok", "no_consent_needed"] as const;
/**
 * NIGHTLY-REVIEW (`cell.nightly_review.status`): one DE's review of one night. `pending` waits
 * for its slot; `running` the review call is out; `done` it filed its proposals (maybe none);
 * `skipped` with a reason (`busy`, `cost_cap`, `no_model`, `no_signal`, `expired`); `failed` the
 * call or the parse failed after its attempts.
 */
export const nightlyReviewStatus = ["pending", "running", "done", "skipped", "failed"] as const;
/**
 * B2-GATES (planned-outputs §3): what a person's gate on a board transition does to a DE's move.
 * `ask` the move becomes a proposal to the task's owner; `hold` the move happens and the owner
 * may pull it back within the gate's window; `trust` the move happens.
 */
export const gateMode = ["ask", "hold", "trust"] as const;
/**
 * B2-GATES: a held move's state (`gate_hold.state`). `open` within its window; `pulled` back by
 * the owner; `passed` its window ended with the move standing (the sweep settles it and the usual
 * status-change notifications go then); `superseded` a later move closed it first.
 */
export const gateHoldState = ["open", "pulled", "passed", "superseded"] as const;
/** AGENT-PLUMBING: a DE's "wake me after N minutes" (`wake_timer.status`). */
export const wakeTimerStatus = ["pending", "fired", "cancelled"] as const;
/**
 * HANDOFF-NO-DROPS: one person's request a DE works through helper cards
 * (`helper_chain.state`). `open` work is moving or waiting on a card; `stalled` the watchdog
 * told the requester it is stuck (it can still move); `delivered` the result reached the
 * requester; `parked` the DE told the requester what blocks it, or a person decides next;
 * `handed` the request moved to someone else. BOARD-GATES: `waiting_person` the request's card
 * sits in a column only a person moves out of (Review, a gated Ideas); still live, never alerted.
 */
export const helperChainState = [
  "open",
  "stalled",
  "delivered",
  "parked",
  "handed",
  "waiting_person",
] as const;
/** HANDOFF-NO-DROPS: a chain's subject — a run's real subject (a task, a topic, a message). */
export const helperChainSubjectKind = ["task", "topic", "message"] as const;
/** SETTLE-SUBJECTS: what a DE settled (`settled_subject.subject_kind`). */
export const settledSubjectKind = ["message", "topic", "thread"] as const;
/** SETTLE-SUBJECTS: who settled it (`settled_subject.how`): the DE said so, or its run ended done. */
export const settledSubjectHow = ["agent", "auto"] as const;
/** HANDOFF-NO-DROPS: a helper card's (or a render job's) last known state. */
export const helperCardState = ["open", "done", "blocked", "crashed"] as const;
/** HANDOFF-NO-DROPS: a kanban card a helper works, or a render-service job. */
export const helperCardKind = ["card", "render"] as const;
/**
 * HANDOFF-NO-DROPS: what a run recorded about the request it holds before it ended (`record_handoff`,
 * or the runtime's own `stopped` with its reason): the run may end "done" only with one of the
 * first five, or with a helper card still open.
 */
export const runHandoffKind = [
  "waiting_on_cards",
  "handed_to",
  "delivered",
  "blocked_told",
  "parked",
  "stopped",
] as const;
/**
 * CLOSED-LOOPS (B3): a commitment's state (`commitment.state`), the A2A task names plus two:
 * `submitted` (asked, not accepted yet), `working`, `input_required` (waits on a named person or
 * agent: an open decision), `auth_required` (waits on an access grant), `in_verification` (a result
 * is in, a verifier other than the doer checks it), and the terminal `completed` (set by the
 * system only, after the verify step passed), `failed` (with a failure report and options),
 * `rejected` (the owner declined at accept), `canceled` (the requester withdrew) and `expired`
 * (its TTL passed with nothing resolved). A terminal state never reopens.
 */
export const commitmentState = [
  "submitted",
  "working",
  "input_required",
  "auth_required",
  "in_verification",
  "completed",
  "failed",
  "rejected",
  "canceled",
  "expired",
] as const;
/** CLOSED-LOOPS: where a commitment lives, when anywhere (the subject a DE is woken on). */
export const commitmentSubjectKind = ["task", "topic", "message"] as const;
/** CLOSED-LOOPS: who made a commitment's change (its log): a party, or the system (a rung). */
export const commitmentLogActorKind = ["person", "digital_employee", "helper", "system"] as const;
/** CLOSED-LOOPS: what a commitment is: a request for work, or a decision someone owes (options). */
export const commitmentKind = ["request", "decision"] as const;
/** CLOSED-LOOPS: who asks or owes: a person, a DE, or a DE's helper (an AG run in its runtime). */
export const commitmentActorKind = ["person", "digital_employee", "helper"] as const;
/**
 * CLOSED-LOOPS: how a result is verified, never by its doer: `mechanical` (the system checks the
 * evidence named), `peer` (a named other agent or person), `requester` (the asker confirms),
 * `human` (a named person).
 */
export const commitmentVerify = ["mechanical", "peer", "requester", "human"] as const;
/**
 * CLOSED-LOOPS: the only reasons to involve a person (design 2026-10-31 §5.1): no access, a service
 * down, a fact only a person holds, two instructions that disagree, a budget spent without
 * converging, a gate or approval.
 */
export const blockerCategory = [
  "access",
  "service",
  "input",
  "conflict",
  "convergence",
  "policy",
] as const;
/** CLOSED-LOOPS: a failure's cause, in one word (the failure report's "what happened"). */
export const commitmentCause = [
  "rejected",
  "no_answer",
  "blocked_on_input",
  "verification_failed",
  "tool_error",
  "deadline_missed",
  "cancelled_by_policy",
  "budget_spent",
  "loop",
] as const;
/**
 * LOOP-BREAKER: why a run was stopped as a loop (`loop_incident.kind`): the same calls with the
 * same results (`repeat`), the same intent over read-only calls (`intent_repeat`), no progress
 * while tokens were spent (`stall`), or the runtime's own in-container guard (`runtime_guard`).
 */
export const loopIncidentKind = ["repeat", "intent_repeat", "stall", "runtime_guard"] as const;
/** LOOP-BREAKER: an incident waits for its owner (`open`), who retries, reassigns or closes it. */
export const loopIncidentStatus = ["open", "retried", "reassigned", "closed"] as const;
/** `approval.state` (the time-entry `approvalStatus` is a different, older vocabulary). */
export const approvalState = ["pending", "approved", "rejected", "edited", "expired"] as const;
/**
 * Phase 1 inc 2: what became of an approved payload, kept apart from the decision. NULL on the
 * row while there is nothing to apply (undecided, rejected, expired); `pending` once approved
 * until it is applied or fails; `applied` and a permanent failure are final.
 */
export const approvalExecutionState = ["pending", "applied", "failed"] as const;
/** A permanent failure is never retried; a transient one is retried (`approval.expire` job). */
export const approvalExecutionFailure = ["permanent", "transient"] as const;
/**
 * Why an approval was not applied (or, `already_satisfied`, applied with nothing to write).
 * The human sentence is `execution_reason`; the code is for clients and tests.
 */
export const approvalExecutionCode = [
  "already_satisfied",
  "unsupported",
  "own_lifecycle",
  "invalid_payload",
  "precondition_missing",
  "stale_version",
  "target_missing",
  "status_invalid",
  "field_invalid",
  "seat_invalid",
  "attachment_mismatch",
  "decider_forbidden",
  "rejected_downstream",
  "could_not_apply",
  "transient_error",
  "retries_exhausted",
  "legacy_unapplied",
  // STEP1-OUTBOX: an external action in the outbox (pending), logged only (applied, no
  // provider), a dead letter (failed, a person retries or cancels it), cancelled from the outbox
  // (final), or refused because its requester holds no grant on the connection (final)
  "queued",
  "dry_run",
  "dead_letter",
  "cancelled",
  "grant_missing",
] as const;
export const delegationState = ["sent", "accepted", "declined", "expired", "completed"] as const;

// finance controls (M3-01; 02 §Finance; 11 §Budget hierarchy, §Controls)
export const budgetSubjectKind = ["company", "team", "seat", "holder", "agent"] as const;
export const budgetState = ["active", "archived"] as const;
export const budgetPeriodType = ["day", "week", "month", "quarter", "year", "standing"] as const;
export const reservationState = ["held", "settled", "released", "expired"] as const;
export const budgetIncidentKind = ["threshold", "cap", "reservation_failed"] as const;
/**
 * DE-BUDGET-PACE: what told a DE's people about its budget — the pace alert, its second
 * (worse) threshold, and the reserve reached (no new runs).
 */
export const deBudgetAlertLevel = [
  "pace",
  "pace_2",
  "reserve",
  // PAUSE-NO-DROP (budget communication): the percent warnings, the surge warning, the DE's
  // last message from its reserve (99 %) and the cap notice (100 %); each once per period.
  "t80",
  "t90",
  "t95",
  "t98",
  "surge",
  "final",
  "cap",
] as const;
export const priceListScope = ["platform", "agency", "company"] as const;
export const priceUnit = [
  "token_in",
  "token_out",
  "cache_read",
  "sandbox_minute",
  "compute_minute",
  "tool_call",
  "storage_gb_month",
  "media_minute",
] as const;

// personal crews (M4-01; 02 §personal_crew, §crew_agent, §role_skill_policy, §autonomy_policy,
// §sharing_policy; 05 §Personal crews; 11 §crew caps)
/** `personal_crew.state`: paused by its owner or by its cap; deactivated with the membership. */
export const personalCrewState = ["active", "paused", "deactivated"] as const;
export const personalCrewPauseReason = ["owner", "budget", "membership"] as const;
export const crewAgentSource = ["role_default", "owner_added"] as const;
/** `autonomy_policy.mode` (05: owner ⊇ role_skill_policy ⊇ autonomy_policy). */
export const autonomyMode = ["alone", "ask", "never"] as const;
/**
 * What a crew may do, the unit of `autonomy_policy` and `role_skill_policy.allowed_action_kinds`.
 * The approval kinds a crew can produce, plus the crew features of 10 §M4.
 */
export const crewActionKind = [
  "message",
  "task_change",
  "page",
  "external_action",
  "delegation",
  "reminder",
  "inbox_triage",
  // CALENDAR-C1 (calendar design §6): `alone` lets the owner's assistant create and move the
  // owner's own events directly; never anyone else's
  "calendar",
] as const;
/** `sharing_policy.rule` (02): what the crew discloses to another principal's agent. */
export const sharingRule = ["never", "status_only", "if_requester_can_see", "always"] as const;
export const sharingSubjectKind = ["task", "message", "dm", "calendar", "memory", "run"] as const;
/**
 * `crew_context_layer.layer` (02 §Reservations; agent-os.md §crew context): the Herk layers,
 * most stable first, which is also the order a run reads them in.
 */
export const crewContextLayer = [
  "manual",
  "identity",
  "business",
  "priorities",
  "voice",
  "connections",
  "decisions",
  "captures",
  "audits",
] as const;
export const crewContextVisibility = ["owner_only", "manager", "company"] as const;
/** The periods a crew cap counts spend over (a subset of `budgetPeriodType`). */
export const crewCapPeriod = ["day", "week", "month"] as const;
// crew features (M4-03; 00 §4, 10 §M4, OPEN-QUESTIONS 37)
/** `crew_proposal.kind`: inbox triage (done, snooze, a drafted reply), a reminder, a task update. */
export const crewProposalKind = ["done", "snooze", "reply", "remind", "task_update"] as const;
/** `pending` waits for the owner; `applied` went through alone; `expired`: its item is gone. */
export const crewProposalState = [
  "pending",
  "accepted",
  "dismissed",
  "applied",
  "expired",
] as const;
/** Who proposed: a crew run (the runtime path) or the deterministic triage rules. */
export const crewProposalSource = ["run", "rule"] as const;
/** OQ 37: the role memory an owner may grant their crew (private notes are never one). */
export const crewMemoryBucket = ["seat", "department", "leadership"] as const;
export const crewMemorySubjectKind = ["seat", "team"] as const;

// agent rooms (M4-05; 06 §Channels "Agent rooms"; ADR-0010)
/** `agent_room.turn_policy`: agents post one after another in `turn_order`. */
export const agentRoomTurnPolicy = ["round_robin"] as const;
/** `agent_room_participant.kind`: agents take turns; people who joined are listed with them. */
export const agentRoomParticipantKind = ["agent", "person"] as const;
/** `status_ask.subject_kind`: what an agent may ask another for the status of. */
export const statusAskSubjectKind = ["task"] as const;
/** `status_ask.state`: delivered (`open`), answered, escalated to a human, or declined by them. */
export const statusAskState = ["open", "answered", "escalated", "declined"] as const;
/** `status_ask.escalation`: refused by the sharing policy, or left unanswered past its deadline. */
export const statusAskEscalation = ["refused", "stuck"] as const;
// finance views (M5-03; 02 §Finance; 11 §Revenue attribution, §Planning)
/** `client.status`, `category.status`, `headcount_plan.status`. */
export const financeRecordStatus = ["active", "archived"] as const;
export const revenueKind = ["invoice", "subscription", "manual"] as const;
export const compensationCadence = ["monthly", "hourly", "contract"] as const;
export const recurringExpenseKind = ["benefit", "overhead", "software_subscription"] as const;
export const recurringCadence = ["monthly", "quarterly", "yearly"] as const;
export const categoryKind = ["income", "expense"] as const;
export const headcountHolderKind = ["person", "digital_employee"] as const;
export const forecastSubjectKind = ["company", "team", "seat"] as const;
/** Finance-only relations (05 §finance): `company#finance_admin`, `seat#comp_viewer`. */
export const financeRelation = ["finance_admin", "comp_viewer"] as const;

// DE configuration (DC-01; 02 §DE configuration; ADR-0018/0019/0020). The tuples match
// `packages/domain-agents/src/schemas.ts` (DEPLOY_STATES, VERDICTS, ENFORCER_KINDS, MODEL_PATHS),
// which the handlers validate with; `de-config.test.ts` asserts the two agree.
/** `agent_template_version.state`: immutable from `validated` on. */
export const templateVersionState = ["draft", "validated", "published", "deprecated"] as const;
export const templateVersionSource = ["author", "learning_loop"] as const;
/**
 * Template class (01; domain-agents TEMPLATE_CLASSES): `managed` is done-for-you (the platform
 * operator sets it up); `empty` and `basic` are self-serve. Rows and events written before
 * migration 0162 may say `htva` for `managed` (contracts/enums.ts `normaliseTemplateClass`).
 */
export const templateClass = ["managed", "basic", "empty"] as const;
export const templateEvalKind = ["fixture", "replay", "live"] as const;
export const templateEvalResult = ["pass", "fail", "error"] as const;
/** 01 "Enforcer", strongest first. */
export const enforcerKind = ["pipeline", "schema", "tool_block", "eval", "written_rule"] as const;
export const templateEditSource = ["reflection", "human"] as const;
export const templateEditState = ["proposed", "accepted", "rejected"] as const;
export const verificationState = ["verified", "unverified"] as const;
export const deployPath = ["done_for_you", "self_serve"] as const;
export const deployKind = ["first", "redeploy", "rollback"] as const;
/** ADR-0018 §5: staged → approved → handed_off → confirmed | failed; staged → rejected. */
export const deployState = [
  "staged",
  "approved",
  "handed_off",
  "confirmed",
  "failed",
  "rejected",
] as const;
/**
 * Phase 2 increment 1: `cp.hermes_instance.key_state`. `proposed` until a deploy of the instance
 * confirmed and a routing probe authenticated with the key (`live`); `retired` only by the
 * operator's recreate-and-reimport path (rotation is not supported).
 */
export const hermesKeyState = ["proposed", "live", "retired"] as const;
/** How the registry row came to be: reserved at a staging, or imported by an operator. */
export const hermesInstanceSource = ["staged", "imported"] as const;
/**
 * Phase 2 increment 3: `cp.panel_profile.source`: allocated at staging, or a legacy name the
 * panel already accepted before allocation existed (backfilled, never renamed).
 */
export const panelProfileSource = ["allocated", "legacy"] as const;
/**
 * SATELLITE-REGISTRY (docs/design/2026-10-30-central-agent-dispatcher.md §4, §5): how CompanyOS
 * reaches a satellite's gateways. `wireguard`: the overlay (plain http allowed on overlay
 * addresses); `mtls`: https over the public internet with a CompanyOS client certificate (the
 * founder's target transport, 2026-10-30; the certificate arrives with SATELLITE-TRANSPORT).
 */
export const satelliteTransport = ["wireguard", "mtls"] as const;
/** `cp.satellite.status`: set by the liveness probe (`live`/`unreachable`) or by an operator. */
export const satelliteStatus = ["live", "unreachable", "draining", "retired"] as const;
/** Who registered a satellite or an agent endpoint: an operator by hand, or the panel intake. */
export const registrySource = ["manual", "panel"] as const;
/** `cp.agent_endpoint.status`: one `active` endpoint per DE; a replaced or retired one is kept. */
export const agentEndpointStatus = ["active", "retired"] as const;
/**
 * SATELLITE-RUNTIME: what a satellite runs at a version the panel installs and reports
 * (`cp.satellite_release.component`). `bridge` and `render_service` are pinned by the platform
 * (`satellite_runtime` setting); `hermes_baseline` is ClawPanel's own baseline, only reported.
 */
export const satelliteReleaseComponent = ["bridge", "render_service", "hermes_baseline"] as const;
/** SATELLITE-RUNTIME: what the panel did when it reported a release. */
export const satelliteReleaseAction = ["installed", "rolled_back", "checked"] as const;
/**
 * AGENT-SPEC (consolidate-then-demo §2, A3): who wrote a version of a DE's agent spec. `editor`:
 * the DE editor (Workforce); `add_de`: the Add-DE flow's first version; `skillsmith`: an accepted
 * Skillsmith proposal; `import`: today's live profile brought in (A12's migration).
 */
export const agentSpecSource = ["editor", "add_de", "skillsmith", "import"] as const;
/**
 * AGENT-SPEC-EXPORT (v2): an applier's report on one profile. `pending`: not applied yet (busy,
 * locked); `applied`: the version runs; `drift`: live differs from the applied version;
 * `shadow_planned`: a shadow run planned changes and wrote nothing; `failed`: the old version
 * keeps running.
 */
export const agentProfileSpecReportStatus = [
  "pending",
  "applied",
  "drift",
  "shadow_planned",
  "failed",
] as const;
/** AGENT-SPEC: the parts of a spec document (`AgentSpecDoc`), each replaced whole by a change. */
export const agentSpecPart = [
  "soul",
  "agents",
  "how_i_work",
  "model",
  "skills",
  "toolsets",
  "approvals",
  "mcp_servers",
  "recall",
  "keys",
  "config",
] as const;
/** DISPATCHER-CORE: `cp.run_dispatch.phase` — claimed (not yet sent), started (prompt built), ended. */
export const dispatchPhase = ["claimed", "started", "ended"] as const;
/** `cp.agent_container.status`: one active row per (satellite, instance); a retired one is kept. */
export const agentContainerStatus = ["active", "retired"] as const;
/** What a container's applier reported for a bundle version. */
export const agentSpecApplyStatus = ["applied", "apply_failed"] as const;
/** The container gateway's health as its applier reported it. */
export const agentContainerHealth = ["ok", "degraded", "down"] as const;
/**
 * Phase 2 increment 6: where `digital_employee.runtime_ref` came from. `manual`: set by hand (every
 * ref that existed before Phase 2 is backfilled `manual`); `deploy`: the explicit opt-in to Hermes
 * execution copied it from the DE's confirmed binding; `local_pinned`: cleared on purpose
 * (`agent:enable --runtime local`, or opting out), so nothing sets it again silently.
 */
export const runtimeRefSource = ["manual", "deploy", "local_pinned"] as const;
/**
 * Phase 2 increment 6: `cp.hermes_probe.state`. `running` while the bounded Hermes run executes;
 * `awaiting` once the nonce came back, until the reporter item (120 s) and the spend row (10 min)
 * arrive; `passed` (evidence published under the generation it captured), `failed` (a named
 * reason), `stale` (the invalidation generation moved before publication: a new probe is needed).
 */
export const hermesProbeState = ["running", "awaiting", "passed", "failed", "stale"] as const;
/**
 * Phase 2 increment 2: `cp.deploy_spend_reader.state`: `active` with its panel key; `retiring`
 * once the key was revoked (kept 7 days for late spend rows); `retired` after its final pull.
 */
export const deploySpendReaderState = ["active", "retiring", "retired"] as const;
/**
 * Phase 2 increment 4: `cp.deploy.reconcile_state` of a deploy closed without a certain panel
 * outcome. `reconcile_needed`: the panel may hold it (a timeout, an unreachable intake, an
 * answer that could not be bound); its credentials are kept and it is polled for 24 h.
 * `late_confirmed`: the panel confirmed it after CompanyOS closed it (not re-opened; the DE's
 * routing evidence is invalidated, a probe required). `late_failed`: the panel failed it late;
 * `not_received`: the panel never had it (not found, with no answer ever recorded). Both last
 * are certain: the credentials are revoked.
 */
/** Phase 2 increment 4: `cp.deploy_callback_pending.type` (the panel's deploy callbacks). */
export const deployCallbackType = ["deploy.confirmed", "deploy.failed"] as const;
export const deployReconcileState = [
  "reconcile_needed",
  "late_confirmed",
  "late_failed",
  "not_received",
] as const;
/** ADR-0018 §4 `error.code` of a failed deploy (the panel's codes plus CompanyOS's own). */
export const deployErrorCode = [
  "bundle_sha_mismatch",
  "bundle_rejected",
  "node_unreachable",
  "health_check_failed",
  "container_company_mismatch",
  "intake_unreachable",
  "intake_unauthorized",
  "handoff_timeout",
  // DC-05: the verification override's `pilot_until` passed before the deploy was handed off.
  "override_expired",
  // Phase 2 increment 2: the seat budget cannot become the panel key's `max_budget` (no money,
  // token-only, or not USD); refused visibly before anything is minted.
  "budget_unsupported",
  "internal",
] as const;
/**
 * `cp.platform_audit.action` (DC-05: the verification override and its expiry; M5-05: the
 * break-glass session's life and every request made under it).
 */
export const platformAuditAction = [
  "verification_override.created",
  "verification_override.expired",
  "break_glass.requested",
  "break_glass.approved",
  "break_glass.rejected",
  "break_glass.ended",
  "break_glass.expired",
  "break_glass.used",
  "home_company.claimed",
  // CONNECTIONS: a platform admin set or reset a service endpoint (who, old → new).
  "service_endpoint.changed",
  // BILLING-PLANS: a platform admin changed a price list (plan / tier version, catalog, defaults).
  "billing_price.changed",
  // WF-CONTROL: a person's Claude Code connection (pairing code made, paired, revoked) and every
  // call of the Workforce Control MCP endpoint (`detail.tool`, `detail.token_id`, `detail.verdict`).
  "control_token.created",
  "control_token.paired",
  "control_token.revoked",
  "control.tool_call",
  // SYSTEM-AGENT: every message a platform super admin writes to a company's system agent (its
  // private space; `detail.message_id`, `detail.kind`, `detail.length` — never the text).
  "system_agent.message",
  "system_agent.key_issued",
  // SKILLSMITH-HUB: a super admin applied, rejected or reverted a report's suggested fix
  // (`detail.report_id`, `detail.change`: what was written and its version — never message text).
  "system_agent.fix_applied",
  "system_agent.fix_rejected",
  "system_agent.fix_reverted",
  // SKILL-PROPOSALS-OPERATOR: a super admin approved or rejected a DE's skill proposal, or marked
  // it shipped (`detail.proposal_id`, `detail.decision`, `detail.work_item_id` — never the text).
  "skill_proposal.decided",
  "skill_proposal.shipped",
  // NIGHTLY-REVIEW: a super admin recorded the client conversation's state on a proposal
  // (`detail.consent`), or sent the client owner the outcome-terms message (`detail.recipients`:
  // a count — never addresses or text).
  "skill_proposal.consent_set",
  "skill_proposal.client_message_sent",
  // SKILLSMITH-OPPORTUNITIES: a super admin sent a client an offer email (`detail.opportunity_id`,
  // `detail.recipients`: a count — never addresses or text), moved an opportunity along the
  // pipeline (`detail.from`, `detail.to`, `detail.amount_minor`), or sent / closed a helper quote.
  "opportunity.offer_sent",
  "opportunity.stage_changed",
  "helper_offer.sent",
  "helper_offer.changed",
  // OPS-AUTH: the platform's ops automation account (a service account, never a person) issued or
  // revoked by a platform super admin from the operator console (`detail.companies`).
  "ops_account.issued",
  "ops_account.revoked",
  // NEW-CLIENT-PROCEDURE: an agency operator seated HTVA operators in a client company and set up
  // its support channel (`detail`: channel name, created, the seats; ids only).
  "support_channel.set_up",
  // PROVISION-IN-PRODUCT: a super admin changed a provisioning setting (platform default or one
  // company's override; `detail.key`, `detail.before`, `detail.after` — settings hold no secret);
  // a company provisioned (its system agent, its feature rules applied: `detail.steps`); the
  // satellite claimed a DE's or system agent's credentials (`detail.key_prefix` only), or
  // registered / removed a DE's bridge (`detail.url`, never the secret).
  "provisioning_setting.changed",
  "company.provisioned",
  "provisioning.credentials_claimed",
  "provisioning.bridge_registered",
  "provisioning.bridge_removed",
  // SATELLITE-REGISTRY: a satellite registered, changed (name, slots, drain) or retired, and an
  // agent endpoint registered or retired, by a platform admin or the panel intake (ids only; a
  // gateway key never appears in `detail`).
  "satellite.registered",
  "satellite.changed",
  "satellite.retired",
  "agent_endpoint.registered",
  "agent_endpoint.retired",
  // SATELLITE-RUNTIME: the panel installed or rolled back a release on a satellite (`detail`:
  // component, version, sha256, drift count; a plain `checked` report is not audited).
  "satellite.release_reported",
  // RECALL-RETENTION-HOLD-FORGET: a legal hold placed by the operator console (`detail.case_ref`,
  // `detail.scope`, `detail.reason`), a release or legal-contact request filed for the operator,
  // the plan retention data changed, and a super admin's hard delete (`detail.target`, the
  // quarantine days; never record text).
  "memory.hold_placed",
  "memory.operator_job_filed",
  "memory.operator_job_closed",
  "memory.retention_changed",
  "memory.hard_delete",
  // OPERATOR-CONSOLE: an operator setting's new version applied or its pointer moved back
  // (`detail.key`, scope, from → to version; settings hold no secret); an operator agent
  // registered or changed (model setting, allowed APIs, cap, status), its API key sealed or
  // removed (`detail.kid` only), a job token issued or revoked (`detail.token_id`, `detail.apis`),
  // a Deployer job queued, and every call an operator agent makes (`operator_agent.action`:
  // `detail.agent`, `detail.job_id`, `detail.api`, `detail.outcome`).
  "operator_setting.changed",
  "operator_setting.rolled_back",
  "operator_agent.registered",
  "operator_agent.changed",
  "operator_agent.key_changed",
  "operator_agent.token_issued",
  "operator_agent.token_revoked",
  "operator_agent.action",
  "operator_job.queued",
  // MONITORING-P1: a stop asked of a run (a person's Stop button or a guard, `detail.reason`),
  // the bridge's answer to it (`detail.ok`, `detail.method`, `detail.latency_ms`), and a guard
  // rule created or changed by a platform admin (`detail.from` / `detail.to`).
  "run.stop_requested",
  "run.stopped",
  "guard_rule.created",
  "guard_rule.changed",
  // AGENT-SPEC: the panel registered a container's scoped identity (or rotated its token) or
  // retired it (`detail`: satellite, instance, token generation; never the token or its hash).
  "agent_container.registered",
  "agent_container.retired",
  // SKILL-LEAK-GUARD: a skill pack version's fingerprints registered or replaced (hash counts
  // only, `detail.files`, `detail.shingles`, `detail.canaries`; never text).
  "skill_fingerprint.registered",
  // SUPER-ADMIN (operator workspace): a super admin assigned an operator to a client or ended the
  // assignment (`detail.user_id`, `detail.company_id`); a client listed or opened from the
  // workspace (`detail.companies` / `detail.tuple`: every access to a client is a row); a
  // super-admin claim link issued from the host's terminal (`detail.expires_at`; never the token
  // or the address) and claimed by the signed-in person it names.
  "operator.assigned",
  "operator.unassigned",
  "operator_workspace.clients_listed",
  "operator_workspace.client_opened",
  "super_admin.claim_issued",
  "super_admin.claimed",
  // SKILLSMITH-LENS: a super admin's (or a report's) read of one company's row-level data through
  // Skillsmith (`detail.tool`, `detail.ids`, `detail.order_id` or `detail.report_id`, the order
  // text); an order taken from the chat; one of the one-click actions (`detail.action`,
  // `detail.proposed_by: skillsmith`, the outcome); a draft filed or decided; a fleet report made;
  // the panel's satellite incident.
  "system_agent.client_read",
  "skillsmith.order",
  "skillsmith.action",
  "skillsmith.draft",
  "skillsmith.report",
  "satellite.incident_reported",
  // SKILLSMITH-CONTROL: a super admin's standing order to one client's Skillsmith — created,
  // paused, resumed, cancelled (`detail.change`), and each scheduled run or refusal
  // (`detail.change = ran | refused`, with the order id). The company is `target_company_id`.
  "skillsmith.standing_order",
  // ONBOARDING-P2: an HQ setup assistant assigned to a client, its assignment revoked (go-live,
  // cancel or reassignment; `detail.reason`), a setup proposal version sent to the client, and the
  // case closed (`detail.outcome`). Ids only.
  "agent.assignment_created",
  "agent.assignment_revoked",
  "onboarding.proposal_sent",
  "onboarding.closed",
] as const;

/** SKILLSMITH-LENS: the three scheduled fleet reports (`cp.fleet_report.kind`). */
export const fleetReportKind = ["daily_alerts", "weekly_fleet", "monthly_skills"] as const;
/**
 * SKILLSMITH-LENS: what became of a report's narrative — `quiet` (nothing to say: no model call),
 * `ok` (it passed the number validator), `rejected` (a number not in the digest: the digest is
 * shown raw), `failed` (the call failed), `off` (no model configured).
 */
export const fleetReportNarrative = ["quiet", "ok", "rejected", "failed", "off"] as const;
/** SKILLSMITH-LENS: a chat question (`question`) or a deep-dive order (`order`) to one company's Skillsmith. */
export const skillsmithOrderKind = ["question", "order"] as const;
export const skillsmithOrderStatus = ["running", "answered", "refused", "failed"] as const;
/**
 * SKILLSMITH-CONTROL: how often a standing order to one client's Skillsmith runs — `once` (one
 * scheduled run, at `run_at`), `weekly` (a weekday and a wall time), `monthly` (a day 1–28 and
 * a wall time), in the order's time zone.
 */
export const skillsmithCadence = ["once", "weekly", "monthly"] as const;
/** SKILLSMITH-CONTROL: `active` (due at `next_run_at`), `paused`, `done` (a run-once that ran), `cancelled`. */
export const skillsmithStandingStatus = ["active", "paused", "done", "cancelled"] as const;
/**
 * SKILLSMITH-LENS: what Skillsmith drafted for the super admin — a config proposal (a setting's
 * old → new; Andrei presses Apply in the console), a builder ticket (Andrei presses Queue), or an
 * action card (one of the one-click actions, fired by the super admin's click). A skill proposal
 * is not here: it goes through the one door (`cell.skill_proposal`, `source = skillsmith`).
 */
export const skillsmithDraftKind = ["config_proposal", "builder_ticket", "action"] as const;
export const skillsmithDraftState = [
  "proposed",
  "accepted",
  "dismissed",
  "done",
  "failed",
] as const;
/** SKILLSMITH-LENS: the one-click safe actions (a settings list may shrink it, never grow it). */
export const skillsmithActionKind = [
  "stop_run",
  "resume_de",
  "pause_schedule",
  "resume_schedule",
  "rewake_task",
  "ack_alert",
  "resolve_alert",
  "retry_operator_job",
] as const;
/** SKILLSMITH-LENS: who wrote a host error line (`cp.service_error.service`). */
export const serviceErrorService = ["api", "worker"] as const;
/** SKILLSMITH-LENS: what ClawPanel reports about a satellite (`cp.satellite_incident.kind`). */
export const satelliteIncidentKind = [
  "container_restart",
  "oom",
  "disk",
  "apply_baseline_failed",
  "gateway_down",
  "other",
] as const;
export const satelliteIncidentSeverity = ["info", "warning", "critical"] as const;

/**
 * OPERATOR-CONSOLE (fleet tuning loop §2.8): every operator setting with a version history in
 * `cp.operator_setting_version`. The provisioning keys (their pointer is the
 * `cp.provisioning_setting` row) and the settings that keep their own store, whose writers add a
 * history row so the console shows one history for all of them.
 */
export const operatorSettingKey = [
  "de_defaults",
  "budget_rollover",
  "feature_rules",
  "wake_intake",
  "system_agent",
  "bridge_runtime",
  "satellite_runtime",
  "line_switches",
  "memory_lane",
  "models",
  "runtime_profiles",
  "container_memory",
  "baseline",
  "roster_overrides",
  "base_prompt",
  "skills_defaults",
  "rollout",
  "limits",
  "nightly_review",
  "failure_stop",
  "skillsmith",
  "recall_tenant",
  "live_canvas",
  "task_budgets",
  // Kept in their own stores (readers unchanged); the history row mirrors each write.
  "effort_defaults",
  "loop_breaker",
  "memory_retention",
  "model_provider_map",
  "plan_model_access",
  "satellite",
] as const;
/** `cp.operator_setting_version.scope`: one value for the platform, a company or a satellite. */
export const operatorSettingScope = ["platform", "company", "satellite"] as const;
/** OPERATOR-CONSOLE: the three operator agents (fleet tuning loop §2.7). */
export const operatorAgentSlug = ["deployer", "fixer", "builder"] as const;
export const operatorAgentStatus = ["active", "paused"] as const;

/**
 * PROVISION-IN-PRODUCT: the provisioning settings a platform super admin edits (platform default,
 * optionally overridden per company). Each key's value shape is `ProvisioningSettingValues` in
 * contracts/api/provisioning.ts; an unset key reads the shipped default.
 */
export const provisioningSettingKey = [
  "de_defaults",
  "budget_rollover",
  "feature_rules",
  "wake_intake",
  "system_agent",
  // SATELLITE-RUNTIME: how the wake bridge runs a self-hosted DE (company override allowed), and
  // the satellite-level runtime (wake port, pinned releases; platform only).
  "bridge_runtime",
  "satellite_runtime",
  // SATELLITE-RUNTIME: the line switches (once hand-edited in a container's line.local.yaml),
  // each on when a skill or integration is present.
  "line_switches",
  // RECALL-INBOX-LOOP: the Memory lane's escalation window (days a card waits with its department
  // before it also goes to the owner/CEO); a company override is allowed.
  "memory_lane",
  // OPERATOR-CONSOLE (§2.8): what was set by hand in files and ops scripts, now versioned rows.
  "models",
  "runtime_profiles",
  "container_memory",
  "baseline",
  "roster_overrides",
  "base_prompt",
  "skills_defaults",
  "rollout",
  "limits",
  // NIGHTLY-REVIEW: the review window (company time), the platform-wide concurrency, the cost cap
  // per company per night, the model and the proposals cap per DE per night.
  "nightly_review",
  // FAILURE-STOP: when a run's repeated tool errors stop it (identical refusals, in a row, per run).
  "failure_stop",
  // SKILLSMITH-LENS: the reports' times and zone, which are mailed, the model, the chat caps and
  // the one-click actions allowed (platform only).
  "skillsmith",
  // NEW-CLIENT-PROCEDURE: whether `recall.provision` may create the company's Recall tenant on a
  // server whose Recall is shared (`RECALL_TENANT_ONLY` set); company scope only, an operator
  // action (`PUT /v1/operator/companies/{id}/recall-tenant`), audited. Replaces the env edit.
  "recall_tenant",
  // CANVAS-CORE: the agentic Live canvas (`/reports/live`), off until a company is switched on.
  "live_canvas",
  // TASK-BUDGETS (roadmap Phase 1 §3): hard per-task limits (tool steps, cost, wall time) for DEs
  // and helpers; enforcement off until a company is switched on.
  "task_budgets",
] as const;

/**
 * PROVISION-IN-PRODUCT: whether a DE or company was made after provisioning shipped (`new`, set up
 * at once) or before (`existing`, left alone until asked). Bound by SATELLITE-RUNTIME.
 */
export const provisioningOrigin = ["new", "existing"] as const;
/** PROVISION-IN-PRODUCT: what a signed panel request's nonce was taken for. */
export const aigentpanelNoncePurpose = ["system_agent_key"] as const;

/** PROVISION-IN-PRODUCT: one DE set-up step's outcome (`cell.de_provisioning.steps`). */
export const provisioningStepState = ["done", "skipped", "failed"] as const;

/** WF-CONTROL: what a profile revision is of (`cell.de_profile_revision.subject_kind`). */
export const profileRevisionSubject = [
  "digital_employee",
  "workforce_agent",
  // TEMPLATES-T3: a client overlay's edits (agent-templates design §2)
  "template_overlay",
] as const;
/** WF-CONTROL: where a profile change came from (the app, or the Workforce Control endpoint). */
export const profileRevisionVia = ["app", "control"] as const;
/**
 * CONNECTIONS (`cp.service_endpoint.key`): the operator-managed service endpoints. Each falls
 * back to its environment variable when nothing is stored (services/api/src/platform/endpoints.ts).
 */
export const serviceEndpointKey = [
  "recall_url",
  "recall_runtime_url",
  "litellm_base_url",
  "aigentpanel_url",
  "hermes_overlay_cidrs",
  "hermes_node_allowlist",
  // ADMIN-INSIGHTS: the model the admins' Ask panel reads questions with (a gateway model name).
  "ask_model",
] as const;

// agency console (M5-05; 02 `break_glass_session`, `portfolio_projection`, `membership_counts`;
// 05 §Agency cross-company access)
/** requested → active → ended | expired; requested → rejected | expired (never approved in time). */
export const breakGlassState = ["requested", "active", "rejected", "ended", "expired"] as const;
/** 05: `scope: company|space|channel`. */
export const breakGlassScope = ["company", "space", "channel"] as const;
/**
 * BREAK-GLASS: what a read under a live session opened (`cell.break_glass_read.subject_kind`):
 * a private channel the operator is not a member of, one of its topics, or a topic's thread.
 */
export const breakGlassReadSubjectKind = ["channel", "topic", "thread"] as const;
/** 12 §Data model `portfolio_projection.kind`. */
export const portfolioProjectionKind = [
  "kpi",
  "finance",
  "goals",
  "issues",
  "escalation",
  "decisions",
  "headcount",
  "meetings",
] as const;
/** `run.verdict` (01 "Verdict"; null when the run produced no output). */
export const verdict = ["verified", "not_verified", "inconclusive"] as const;
/** `run.model_path` (01 "Model path"; ADR-0020). */
export const modelPath = ["gateway", "subscription"] as const;
/**
 * MODEL-KEYS-PLANS: `run.model_path` adds `own_key` — the company's own provider key (Connections
 * › API keys), billed by its provider, never through the wallet. Templates keep `modelPath`.
 */
export const runModelPath = [...modelPath, "own_key"] as const;
/** MODEL-KEYS-PLANS: `company.settings.model_access.mode` (ours = our keys, billed; own = theirs). */
export const modelAccessMode = ["ours", "own"] as const;
export const runDecisionResult = ["ok", "failed", "unknown"] as const;
export const runEscalationKind = ["premise"] as const;
export const driftAuditTrigger = ["schedule", "target_changed", "manual"] as const;
export const driftAuditState = ["scheduled", "running", "passed", "failed", "error"] as const;
/** IMPORT: the export an import run reads (Setup › Integrations › Import). */
export const importRunSource = ["zulip", "clickup"] as const;
/** IMPORT: `import_run.status` (uploaded → queued → running → succeeded | failed | cancelled). */
export const importRunStatus = [
  "uploaded",
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
] as const;
/**
 * cp `model_credential.scope` (ADR-0020 widened, founder 2026-10-20): a person's own
 * credential, or a company's. `cli_subscription` is person-only (research 2026-10-20 §4.3).
 */
export const modelCredentialScope = ["person", "company"] as const;
/**
 * `api_key`: a provider API key, sealed in the keyring, used upstream by the gateway.
 * `cli_subscription`: a plan login that lives inside the vendor's unmodified CLI on a runtime
 * session or the person's device; CompanyOS holds a reference and an attestation, never a token.
 */
export const modelCredentialKind = ["api_key", "cli_subscription"] as const;
/** Where a `cli_subscription` login lives (research 2026-10-20 §4.2 options D and G). */
export const cliLoginLocation = ["runtime_session", "user_device"] as const;
// MODEL-KEYS-PLANS: `superseded` — an older live company key of a provider that already had a newer
// one when "one key per provider" arrived (migration 0162): kept sealed, never used, never deleted.
export const modelCredentialState = ["active", "revoked", "superseded"] as const;
/**
 * cp `tool_connection` (BYO-01; Work OS `connect`, lifted in `packages/domain-agents/src/connect`):
 * whose connection it is, which provider, and the account state Work OS's health is derived from.
 */
export const toolConnectionScope = ["person", "company"] as const;
// GHL-PROVIDER (build order increment 2 B): `ghl` — a GoHighLevel location, by a Private
// Integration token or the marketplace app's OAuth (Location or Company token).
export const toolConnectionProvider = ["api_key", "google", "github", "slack", "ghl"] as const;
export const toolConnectionState = [
  "healthy",
  "rate_limited",
  "unreachable",
  "reauth_required",
  "revoked",
  "disconnected",
] as const;
/**
 * cell `crew_connection_grant` (M4-04): what a crew's agent may read through its owner's own
 * connection — the owner's mail or calendar, nothing else.
 */
export const crewConnectionKind = ["mail", "calendar"] as const;
/**
 * STEP1-OUTBOX (integrations §2.6): who a `connection_grant` names — a person or a DE by
 * membership, a seat (its holders), a team (the holders of its seats) or a personal crew.
 */
export const connectionGranteeKind = ["membership", "seat", "team", "crew"] as const;
/** What a grant lets its grantee do: read through it, act through it, or re-grant it. */
export const connectionGrantLevel = ["use_read", "use_act", "manage"] as const;
/** The tool families a grant may open (empty `families`: all the connection serves). */
export const connectionFamily = ["mail", "calendar", "crm", "message", "api"] as const;
/**
 * STEP1-OUTBOX: the outward action an `external_action` approval carries (its family sub-kind).
 * Every outward act goes proposal → policy or approval → outbox → executor → result → audit.
 */
export const externalActionKind = [
  "mail.send",
  "crm.write",
  "message.send",
  "api.request",
] as const;
/**
 * cell `outbox` row: `queued` (waits for `send_at` or its next attempt), `sending` (leased to
 * the drain), `sent` (final), `failed` (a transient failure, retried with backoff), `dead` (a
 * dead letter: a person retries or cancels it), `cancelled` (final).
 */
export const outboxStatus = ["queued", "sending", "sent", "failed", "dead", "cancelled"] as const;
/**
 * INC2-INTAKE (build order §3 "One intake"; integrations §2.4): where an `inbound_event` came
 * from. `hook` an incoming webhook delivery, `link` a booking link, `mailbox` a mailbox's new
 * mail, `form` a form submission, `ghl_location` / `hubspot_portal` / `google` a vendor's change
 * notice for that account or resource, `chat_message` a message from a chat channel (build order
 * increment 2b "Channels as surfaces": Slack, Telegram; the source key is the channel binding's
 * id, the provider is on the event's label and subscription — reserved here so 2b needs no
 * schema change). Each source adds a verification preset and a default handler rule, nothing
 * else.
 */
export const inboundSourceKind = [
  "hook",
  "link",
  "mailbox",
  "form",
  "ghl_location",
  "hubspot_portal",
  "google",
  "chat_message",
  // ONBOARDING-P4: a client's system address after go-live. Never a task for a client DE: the HQ
  // dispatcher takes it (domain/hq-dispatch), the intake consumer only logs it.
  "system_address",
] as const;
/**
 * A handler rule's kind: a source kind (that source's default handler DE), `owner` (a person's
 * assistant DE: the first fallback for the sources that person owns) or `company` (the Chief of
 * Staff: the last fallback).
 */
export const inboundRuleKind = [...inboundSourceKind, "owner", "company"] as const;
/**
 * `inbound_event.status`: `received` (waits for the intake consumer), `tasked` (a task for its
 * handler), `logged` (no rule names a handler for its source: kept, nobody woken), `dropped` (a
 * rule, but no handler with a seat or no live board: the reason is on the row).
 */
export const inboundEventStatus = ["received", "tasked", "logged", "dropped"] as const;
/** Which link of the handler chain took the event: its rule, the owner's assistant, the CoS. */
export const inboundHandlerVia = ["rule", "owner_assistant", "chief_of_staff"] as const;
/**
 * How a notice or delivery was verified: the incoming-webhook modes, the vendor presets of the
 * notify receiver (Google channel token, HubSpot v3 HMAC, GoHighLevel Ed25519, CompanyOS
 * `t=,v1=`), a person's own entry (`sample`, `replay`) and `internal` (a native source written
 * in-process: a booking, a form).
 */
export const inboundVerification = [
  "companyos",
  "stripe",
  "github",
  "google_channel",
  "hubspot_v3",
  "ghl_ed25519",
  "sample",
  "replay",
  "internal",
] as const;
/** The verification presets a notify subscription may use (integrations §2.4). */
export const notifyPreset = ["companyos", "google_channel", "hubspot_v3", "ghl_ed25519"] as const;
/** integrations §2.4: how a vendor tells us of a change. */
export const integrationSubscriptionKind = ["webhook", "subscription", "poll"] as const;
export const integrationSubscriptionState = ["live", "renewing", "lapsed", "paused"] as const;
/** integrations §2.5: what a job does through its connection. */
export const integrationJobKind = ["pull", "push", "act", "renew"] as const;
/**
 * `integration_job.state`: `queued` (due at `next_at`), `running` (leased), `done` (final),
 * `dead` (a dead letter: a person retries or discards it), `discarded` (final).
 */
export const integrationJobState = ["queued", "running", "done", "dead", "discarded"] as const;
/**
 * A vendor call's failure class (Work OS `ErrorClass`): `unavailable` and `rate_limited` are
 * retried on the webhook schedule; `reauth`, `scope_lost` and `request` (the request's own
 * fault) are dead at once; `no_handler` (nothing in this build pulls for that provider yet)
 * waits without charging an attempt.
 */
export const integrationErrorClass = [
  "unavailable",
  "rate_limited",
  "reauth",
  "scope_lost",
  "request",
  "no_handler",
  "internal",
] as const;

// cell: the thin contact core (INC2-CONTACT; CRM design §2.1, §2.5, §2.6; build order inc. 2 C)
/** `contact.lifecycle` (CRM §2.1, contact only). */
export const contactLifecycle = ["lead", "prospect", "customer", "past", "other"] as const;
/** Where a contact or organisation came from (CRM §2.1 `record.source`). */
export const contactSource = [
  "manual",
  "agent",
  "booking",
  "form",
  "import",
  "sync",
  "chat",
  "merge",
] as const;
/**
 * `contact_identity.kind`: an email or phone of a contact, an organisation's domain, or an id the
 * contact or organisation has in an outside system (GHL, HubSpot) through a connection.
 */
export const contactIdentityKind = [
  "email",
  "phone",
  "domain",
  "external",
  // Increment 2b "Channels as surfaces": chat users, held by a contact or by a person (membership)
  "slack_user",
  "telegram_user",
] as const;
/** `contact_consent.channel` (CRM §2.6). */
export const consentChannel = ["email", "sms", "call", "whatsapp", "mail"] as const;
export const consentStatus = ["opted_in", "opted_out", "unknown"] as const;
export const consentBasis = ["explicit", "form", "booking", "imported", "inferred"] as const;
/** `contact_duplicate.state` (CRM §2.5 `duplicate_candidate`). */
export const contactDuplicateState = ["open", "dismissed", "merged"] as const;

// cell: measurement
export const metricUnit = [
  "number",
  "currency",
  "percent",
  "yes_no",
  "done_not_done",
  "time",
  "rating",
] as const;
export const metricDirection = ["gte", "lte", "gt", "lt", "eq", "between"] as const;
export const metricAggregation = ["cumulative", "average"] as const;
export const metricNormalization = [
  "worked_days",
  "working_days_in_month",
  "calendar_days",
] as const;
export const metricCadence = ["daily", "weekly", "monthly", "quarterly", "yearly"] as const;
/** SPEND-KPIS: `ledger` = fed by the `spend.snapshot` job from the cost ledger, never typed in. */
/** T3-TASK-ROLLUPS: `task` = counted from a board's tasks by `task_metric.snapshot` (`task_query`). */
export const metricSource = ["event", "manual", "formula", "import", "ledger", "task"] as const;
export const visibility = ["client", "agency"] as const;
export const gapPolicy = ["excuse", "count"] as const;
export const trackingMode = ["off", "context", "scored"] as const;
/** AGENT-REPORTING (13 §Reporting duties): how a seat measurable's number gets reported. */
export const reportingMethod = ["agent_tool", "manual", "import", "events"] as const;
/** AGENT-REPORTING: who a reporting duty reminds before it is due. */
export const reportingRemind = ["holder", "supervisor"] as const;
/** TEAM-KPI (founder-approved 2026-10-21): a team is a measurement subject too. */
/**
 * HELPER-KPIS (design 2026-11-02 §4): `agent` = a helper (`workforce_agent.id`), whose numbers
 * the platform derives from board events attributed to it (`event.actor_helper_id`).
 */
export const subjectKind = ["seat", "company", "team", "agent"] as const;
/** TEAM-KPI: a team KPI's value — the team's own, or its member seats' added or averaged. */
export const teamRollup = ["none", "sum", "average"] as const;
/** KPI-VISIBILITY (05 §KPI visibility, founder-approved 2026-10-22): who a grant names. */
export const kpiPrincipalKind = ["role", "seat", "team", "person"] as const;
/** KPI-VISIBILITY: which seats a grant opens — own, branch (down), peers, up the chain, all. */
export const kpiScope = ["own", "branch", "peers", "up", "everything"] as const;
/** KPI-VISIBILITY: the dimension groups a grant applies to. */
export const kpiGroup = ["kpi", "priorities", "tasks", "finance"] as const;
export const periodType = ["day", "week", "month", "quarter", "year", "round"] as const;
export const observationStatus = [
  "on_time",
  "late",
  "backfilled",
  "override",
  "projected",
] as const;
/** AGENT-KPI-TOOL: `agent` = a value a DE's own run recorded (`outcomes.record`, with provenance). */
export const observationSource = ["event", "manual", "import", "agent", "ledger", "task"] as const;
/** SPEND-KPIS: what a `spend_daily` row sums — the company, a team, a seat or one agent. */
export const spendSubjectKind = ["company", "team", "seat", "agent"] as const;
/** T3-TASK-ROLLUPS: what a `task_metric_daily` row counts — the whole board, or one assignee seat. */
export const taskMetricSubjectKind = ["board", "seat"] as const;
export const fridayReportState = ["draft", "generated", "sent", "failed"] as const;

// cell: cost and time
export const costEventKind = [
  "de_subscription",
  "de_usage",
  "agent_tokens",
  "agent_compute",
  "agent_tool",
  // M3-01 (02 §cost_event: the agent kinds; ADR-0020 §4: subscription runs post `estimated`)
  "agent_sandbox",
  "agent_storage",
  "agent_media",
  "estimated",
  "human_time",
  "adjustment",
  "reversal",
] as const;
export const subscriptionCadence = ["monthly"] as const;
export const timeEntryKind = ["work", "supervision", "founder", "setup", "other"] as const;
export const timeEntryGrain = ["interval", "day", "week"] as const;
// DECISION-LEDGER: `review`, a supervisor's review of an agent's approval (open → decision),
// written by the record's feeder as approved supervision time.
export const timeEntrySource = [
  "manual",
  "import",
  "timer",
  "tracker",
  "workpulse",
  "review",
] as const;
export const approvalStatus = ["approved", "pending", "rejected"] as const;

// cell: events
/** M4-01 (02 Conventions): `personal_agent` is a crew acting through its run. */
export const actorKind = [
  "person",
  "digital_employee",
  "personal_agent",
  "system",
  "agency_user",
] as const;
export const consumerState = ["retrying", "done", "failed", "skipped_by_operator"] as const;

// cell: memory and Recall (M7-03; 02 §Integrations, §Knowledge, §Goals and reminders; 07)
/**
 * `recall_link.subject_kind`. Spec 02 lists four; spec 07's bucket scopes, the per-tenant webhook
 * secret and CompanyOS's own service agent (ingest + callback reads) need rows of their own
 * (`@companyos/recall` `recallSubjectKinds`, kept equal by `tables/recall-link.test.ts`).
 */
export const recallSubjectKind = [
  "company",
  "membership",
  "company_bucket",
  "space",
  "team",
  "seat",
  "agency_bucket",
  "digital_employee",
  "webhook",
  "service_agent",
  // M7-05: a person's crew holds a Recall agent of its own (OQ 37 role-memory grants).
  "personal_crew",
  // RECALL-FOLLOWUP: a tenant user key (`uk_`) the events poller reads `GET /tenant/events` with.
  "events_reader",
  // AG-MEMORY (design 2026-10-31 §0.3): a helper's own Recall agent (`ag:{id}`, secret = rk_) and
  // its own memory space (`agent:{id}`); the subject is the `workforce_agent` id.
  "workforce_agent",
  "agent_bucket",
] as const;
export const recallIssuanceState = ["none", "pending", "acked", "voided"] as const;
/** `recall_memory_copy.state` (M7-05): a hire-as-DE copy waits, is done, or failed for good. */
export const recallMemoryCopyState = ["pending", "done", "failed"] as const;
/** `recall_ingest_item.bucket_kind` (RECALL-FOLLOWUP): the bucket kinds of `recall_link`. */
export const recallIngestBucketKind = [
  "company_bucket",
  "space",
  "team",
  "seat",
  "agency_bucket",
] as const;
/** `recall_capture_thread.source` (CAPTURE-LESS-NOISE): the capture setting a conversation falls under. */
export const recallCaptureSource = ["chat", "task_threads", "runs"] as const;
/**
 * `recall_mirror.kind` (RECALL-INGEST-ALL, design 2026-10-31 §0.2): the objects of record that are
 * written to memory as ONE superseding record each (`task:{id}`, `approval:{id}`, ...).
 */
export const recallMirrorKind = [
  "task",
  "approval",
  "decision",
  "meeting",
  "priority",
  "crm_record",
] as const;
/**
 * `recall_capture_log.source`: the conversation sources, the mirrored objects, the CRM activity
 * timeline (batched ingest), and `other` (the remaining batched events). One count per source is
 * the memory health metric.
 */
export const recallCaptureLogSource = [
  ...recallCaptureSource,
  ...recallMirrorKind,
  "crm_activity",
  "other",
] as const;
/** `recall_capture_log.outcome`: what one capture decision did. */
export const recallCaptureOutcome = [
  "sent",
  "skipped_no_person",
  "skipped_off",
  "unchanged",
] as const;
/** `recall_kb.agent_access` (RECALL-KB): Recall's `agent_access` of a synced space's knowledge base. */
export const recallKbAgentAccess = ["none", "recall", "record"] as const;
/** `memory_retention.state` (RECALL-RETENTION-HOLD-FORGET): the policy's mirror to the tenant. */
export const memoryRetentionState = ["pending", "mirrored", "waiting", "failed"] as const;
/** `memory_hold.scope`: a space, a person or the workspace (no tenant scope). */
export const memoryHoldScope = ["space", "person", "workspace"] as const;
/** `memory_hold.state`: `waiting` = the memory service does not offer holds yet. */
export const memoryHoldState = ["waiting", "placed", "release_requested", "released"] as const;
/** `memory_forget_request.mode`: a forget, or a super admin's hard delete (7-day quarantine). */
export const memoryForgetMode = ["forget", "hard"] as const;
export const memoryForgetScope = ["record", "conversation", "person", "space"] as const;
export const memoryForgetState = [
  "waiting",
  "previewed",
  "blocked",
  "confirmed",
  "undone",
  "expired",
  "failed",
] as const;
/** `cp.operator_job.kind`: what Recall allows a signed-in person only, and the monthly report. */
/**
 * `cp.operator_job.kind`: the memory jobs a super admin closes by hand (RECALL-RETENTION-HOLD-
 * FORGET) and, OPERATOR-CONSOLE, the operator agents' jobs (the Deployer's two).
 */
export const operatorJobKind = [
  "hold_release",
  "legal_contact",
  "retention_report",
  "company_from_pack",
  "apply_baseline",
] as const;
/** `open`/`closed`: the memory jobs; `queued → running → done | failed`: an operator agent's. */
export const operatorJobState = ["open", "closed", "queued", "running", "done", "failed"] as const;
/**
 * `agent_memory_write.kind` (D15, preview): the only things an agent may write to memory. A
 * lesson always goes to a person first (an approval of kind `memory_lesson`).
 */
export const agentMemoryKind = ["lesson", "decision", "pattern", "fact", "correction"] as const;
/** `agent_memory_write.evidence_kind` (D15): what a write rests on. */
export const agentMemoryEvidenceKind = ["run", "task", "message"] as const;
/**
 * `agent_memory_write.state` (D15): `queued` waits for the flush to send it; `proposed` waits for
 * a person (a lesson); `sent` reached Recall; `rejected` a person said no (or the approval
 * expired); `dropped` its bucket ended or Recall refused it for good.
 */
export const agentMemoryWriteState = ["queued", "proposed", "sent", "rejected", "dropped"] as const;
/**
 * `recall_memory_outbox.target_kind` (D14+D15): the space a queued agent memory is aimed at, by
 * its `recall_link.subject_kind` (an agent's seat space, a department, a project space, the
 * company level, the agency) or `personal` (a person's personal space; the subject is the person).
 */
export const recallOutboxTargetKind = [
  "seat",
  "team",
  "space",
  "company_bucket",
  "agency_bucket",
  "personal",
] as const;
/**
 * `recall_memory_outbox.writer_kind`: who writes it, fixed when it is queued. `agent`: the DE's
 * or crew's own Recall key; `service`: CompanyOS's service agent; `person`: the person, through
 * the install key and their own assertion (R1 `write`).
 */
export const recallOutboxWriterKind = ["agent", "service", "person"] as const;
/**
 * `recall_memory_outbox.state`: `pending` goes at the next flush (in order); `held` waits for a
 * person or for Recall (`held_reason`): never dropped, never retried blindly.
 */
export const recallOutboxState = ["pending", "held"] as const;
/**
 * RECALL-RULES (`memory_rule.scope`): a standing rule binds every agent of the company, or one DE.
 */
export const memoryRuleScope = ["company", "de"] as const;
/**
 * RECALL-RULES (`memory_rule.status`): `proposed` waits for a person (a `memory_rule` approval);
 * `active` binds; `dismissed` was turned down; `lifted` no longer binds (kept as history).
 */
export const memoryRuleStatus = ["proposed", "active", "dismissed", "lifted"] as const;
/**
 * RECALL-RULES (`memory_rule.source`): where a rule was stated. `setup` Setup › Recall; `de_page`
 * the DE's page; `chat` a message ("Make this a rule"); `de_suggestion` a DE's `suggest_rule`;
 * `recall_proposal` a proposal an agent filed in Recall itself.
 */
export const memoryRuleSource = [
  "setup",
  "de_page",
  "chat",
  "de_suggestion",
  "recall_proposal",
] as const;
/**
 * RECALL-RULES / RECALL-PROCEDURES: how far a CompanyOS record reached Recall. `pending` waits for
 * the worker; `synced` Recall holds it; `refused` Recall answered no (the detail says why; tried
 * again later); `not_offered` this Recall has no door CompanyOS's keys may use for it yet.
 */
export const recallPushState = ["pending", "synced", "refused", "not_offered"] as const;
/** RECALL-PROCEDURES: how one run of a procedure went (Recall's `procedure_run` words). */
export const procedureRunResult = ["succeeded", "failed", "abandoned"] as const;
/** The CompanyOS scope of a Recall bucket (07 §Tenancy: `company:|space:|team:|seat:|agency:`). */
/** AG-MEMORY: `agent`, a helper's (AG) own memory space (`agent:{workforce_agent id}`). */
export const memoryScopeKind = ["company", "space", "team", "seat", "agency", "agent"] as const;
/** A Recall proposal offered as a Knowledge page (03 `promotion.*`). */
/** MEMORY-HYGIENE: `expired`, a page candidate nobody decided within 14 days. */
export const promotionState = ["pending", "accepted", "rejected", "expired"] as const;
/**
 * MEMORY-HYGIENE: one Recall proposal waiting for review (`memory_proposal`): `approved` and
 * `dismissed` are a reviewer's (one by one or in bulk), `expired` is the 14-day clock's.
 */
export const memoryProposalState = ["pending", "approved", "dismissed", "expired"] as const;
/** How a memory proposal was settled. */
export const memoryProposalDecidedHow = [
  "person",
  "bulk_exact_merges",
  "bulk_retired_agents",
  "expired",
  // MEMORY-RECONCILE: Recall no longer holds it pending (applied, rejected or expired there).
  "recall",
] as const;
/**
 * RECALL-CONTRADICTIONS: one open contradiction Recall lists (`memory_contradiction`): `settled`
 * is a person's verdict in CompanyOS; `gone` is Recall no longer listing it (settled elsewhere,
 * a side retired) before anyone settled it here.
 */
export const memoryContradictionState = ["open", "settled", "gone"] as const;
/**
 * How a person settled a contradiction: keep one side (the other is superseded), `merge` (both
 * hold, each under the condition the person wrote), or `dismiss` (not a real conflict).
 */
export const memoryContradictionAction = ["keep_a", "keep_b", "merge", "dismiss"] as const;
/**
 * PLAN-CHECK: Recall's verdict on a plan (`conflict`, `caution`, `no_conflict_found`, `unjudged`),
 * plus `unavailable` when Recall could not be asked.
 */
export const planCheckVerdict = [
  "conflict",
  "caution",
  "no_conflict_found",
  "unjudged",
  "unavailable",
] as const;
/**
 * RECALL-INBOX-LOOP: what a Memory lane card is. `lesson` an agent's lesson or record to keep,
 * `candidate` a page to promote, `dream` Recall's own consolidation proposal, `rule` a rule
 * proposal or confirmation, `settle` a contradiction with a settle suggestion, `contradiction` one
 * without, `outcome` a decision's "did it work" check.
 */
export const memoryLaneKind = [
  "lesson",
  "candidate",
  "dream",
  "rule",
  "settle",
  "contradiction",
  "outcome",
  "archive",
] as const;
/** The person's answer to a lane card (`edit_approve`: approved with their own words). */
export const memoryLaneAction = [
  "approve",
  "reject",
  "edit_approve",
  "worked",
  "did_not",
  "too_early",
] as const;
/**
 * A lane decision waiting for a Recall door that is not live: `queued` (kept here, retried
 * daily), `sent` (Recall has it), `dropped` (the card went away before it could be sent).
 */
export const memoryLaneDecisionState = ["queued", "sent", "dropped"] as const;
/**
 * RECALL-ADMIN-MEMORY (`memory_admin_act.kind`): what an admin did in Setup › Memory that CompanyOS
 * keeps a trail of. `record_*` are done at once through the install doors (flag, archive,
 * restore). `batch_settle` and `clear_injection_flags` stay signed-in only in Recall, so they are
 * filed as an operator job (`open` until the operator has done it in Recall).
 */
export const memoryAdminActKind = [
  "record_flag",
  "record_archive",
  "record_restore",
  "batch_settle",
  "clear_injection_flags",
] as const;
/** `memory_admin_act.state`: a record act is `done` or `refused`; an operator job is `open`, `done` or `failed`. */
export const memoryAdminActState = ["open", "done", "refused", "failed"] as const;
/**
 * MEMORY-QUALITY-WATCH (`memory_quality_sample.scope`): a sample is of the whole company (`tenant`),
 * one space (`space`, keyed like the Setup › Memory space keys) or one writer (`writer`, a
 * membership id).
 */
export const memoryQualityScope = ["tenant", "space", "writer"] as const;
/**
 * MEMORY-QUALITY-WATCH (`memory_quality_finding.kind`): what the watch or the support sweep found.
 * The watch raises `flood`, `duplicates`, `stale_facts`, `contradictions`, `proposals`, `brief`,
 * `reads` and `why`; the support sweep proposes `settle_suggestions`, `archive_set`, `charter`,
 * `rule` and files `inert_grants` and `housekeep` (both wait on the memory service's admin act).
 */
export const memoryQualityFindingKind = [
  "flood",
  "duplicates",
  "stale_facts",
  "contradictions",
  "proposals",
  "brief",
  "reads",
  "why",
  "settle_suggestions",
  "archive_set",
  "charter",
  "rule",
  "inert_grants",
  "housekeep",
] as const;
/** `memory_ingest_ledger.source`: which ingest sent the captures (chat, or the task threads). */
export const memoryIngestSource = ["chat", "task_threads"] as const;
/** `memory_quality_finding.source`: the hourly watch, or the support sweep. */
export const memoryQualityFindingSource = ["watch", "sweep"] as const;
/** `memory_quality_finding.state`: open until it no longer holds (or the operator closes it). */
export const memoryQualityFindingState = ["open", "resolved", "dismissed"] as const;
/**
 * MEMORY-QUALITY-WATCH (`memory_archive_suggestion.state`): `open` waits for the owner's click in
 * the Memory lane; `approved` is the yes (the `memory.facts_check` job archives it, retrying while
 * the memory service is away); `applied` is archived; `dismissed` the owner said keep them; `gone`
 * the records no longer exist (archived elsewhere, or corrected); `failed` the memory service
 * refused it for good (a legal hold).
 */
export const memoryArchiveSuggestionState = [
  "open",
  "approved",
  "applied",
  "dismissed",
  "gone",
  "failed",
] as const;
/** `memory_archive_suggestion.source`. */
export const memoryArchiveSuggestionSource = ["facts_check", "sweep"] as const;
/** A decision's "did it work" answer. */
export const decisionOutcomeAnswer = ["worked", "did_not", "too_early"] as const;
/** 02 `decision.source`, plus `recall` (07 §Webhooks: `decision.outcome` → the decision log). */
export const decisionSource = ["meeting", "issue", "approval", "manual", "recall"] as const;
/**
 * RECALL-KPIS-MIRROR: what a mirror row stands for in Recall (`company_record`): a priority
 * (Rock), an issue (Issue), or one metric of one seat, team or the company (Metric).
 */
export const recallKpiMirrorKind = ["priority", "issue", "metric"] as const;
/**
 * RECALL-KPIS-MIRROR: `mirrored` Recall holds what CompanyOS holds; `waiting` the record exists
 * but an owner's change waits (no accountable person, or Recall does not offer the `company` act
 * yet); `held` Recall refused it (the reason in `detail`, never sent again until it changes).
 */
export const recallKpiMirrorState = ["mirrored", "waiting", "held"] as const;
/**
 * RECALL-BRIEFS: what a memory block is for. `de_brief` and `cos_report`: the memory part of a DE's
 * morning brief and of the CoS report (subject: the DE); `catch_up`: a person's "While you were
 * away" card (subject: the person; one per window); `space_summary`: a space's weekly summary
 * (subject: the space; one per week); `top_of_mind`: the top of mind set from KPIs (subject: the
 * space).
 */
export const recallMemoryBlockKind = [
  "de_brief",
  "cos_report",
  "catch_up",
  "space_summary",
  "top_of_mind",
] as const;
/** RECALL-BRIEFS: `membership` (a DE or a person) or the CompanyOS kind of a space. */
export const recallMemoryBlockSubject = [
  "membership",
  "company_bucket",
  "space",
  "team",
  "seat",
] as const;
/**
 * RECALL-BRIEFS: `ready` the block is read; `unavailable` memory did not answer (why in `detail`);
 * `waiting` it needs something Recall does not offer yet (the `me` act); `posted` a space summary
 * is in its channel; `dismissed` the person closed a catch-up card.
 */
export const recallMemoryBlockState = [
  "ready",
  "unavailable",
  "waiting",
  "posted",
  "dismissed",
] as const;
/**
 * RECALL-ROLES-ONBOARDING: what a queued person act is. The seat's role (`role_create`,
 * `role_rename`, `role_retire`), its memory handed on (`succession_propose` by the owner,
 * `succession_answer` accept or decline by the successor, `succession_settle` cancel or apply by
 * an admin), an acting holder's cover ended (`acting_end`), a person's profile (`profile_set`),
 * one onboarding answer (`onboarding_answer`), who knows what (`knows_about`), a space's card
 * (`card_set`) and charter (`charter_set`).
 */
export const recallActKind = [
  "role_create",
  "role_rename",
  "role_retire",
  "succession_propose",
  "succession_answer",
  "succession_settle",
  "acting_end",
  "profile_set",
  "onboarding_answer",
  "knows_about",
  "card_set",
  "charter_set",
] as const;
/**
 * RECALL-ROLES-ONBOARDING: `queued` waits (for the memory service to offer the door, or for its
 * next try; `detail` says which); `sent` Recall took it; `held` Recall refused it (why in
 * `detail`; it is not sent again until the request changes); `dropped` no longer wanted.
 */
export const recallActState = ["queued", "sent", "held", "dropped"] as const;
/** RECALL-ROLES-ONBOARDING: a seat's role in Recall. */
export const recallRoleState = ["pending", "mirrored", "retired"] as const;
/**
 * RECALL-ROLES-ONBOARDING: a seat's memory handed to its new holder. `waiting` not proposed to
 * Recall yet; then Recall's own states.
 */
export const recallSuccessionState = [
  "waiting",
  "proposed",
  "accepted",
  "declined",
  "cancelled",
  "applied",
] as const;
/** RECALL-ROLES-ONBOARDING: the "Is this you?" draft of a person's profile. */
export const recallProfileDraftState = ["draft", "accepted", "discarded", "expired"] as const;
/** RECALL-ROLES-ONBOARDING: the owner `deep`, an admin `medium`, anyone else `quick`. */
export const recallOnboardingDepth = ["deep", "medium", "quick"] as const;
/**
 * RECALL-ROLES-ONBOARDING: `offered` a card waits for a yes; `asking` the CoS DE is asking in
 * chat; `done`; `dismissed` the person said not now (members only; the owner's and admins' flows
 * are not dismissable).
 */
export const recallOnboardingState = ["offered", "asking", "done", "dismissed"] as const;
/** RECALL-ROLES-ONBOARDING: where a charter came from. */
export const recallCharterSource = ["creation", "vision", "manual"] as const;
/** RECALL-ROLES-ONBOARDING: `waiting` is not in memory yet; `set`; `held` Recall refused it. */
export const recallCharterState = ["waiting", "set", "held"] as const;
/** RECALL-ROLES-ONBOARDING: a charter belongs to the company space or to one space. */
export const recallCharterSubject = ["company_bucket", "space"] as const;
/** RECALL-KPIS-MIRROR: a person's stance on a logged decision. */
export const decisionStanceKind = ["ratify", "dissent"] as const;
/**
 * RECALL-KPIS-MIRROR: what Recall did with a stance: `sent`, `waiting` (Recall does not offer the
 * `company` act yet, or the person is not set up in memory), `refused` (Recall said no, why in
 * `recall_detail`), `local` (the decision has no record in memory to ratify).
 */
export const decisionStanceRecallState = ["sent", "waiting", "refused", "local"] as const;
/** M4-03: `crew` — a reminder the owner's crew set (`origin_ref` = its proposal). */
export const reminderOrigin = [
  "manual",
  "recall_commitment",
  "crew",
  // COS-SLICE1: a person accepted a follow-up card (`origin_ref` = the suggestion).
  "suggestion",
] as const;

// ── Parity P1 (task detail, layout, prefs, undo) ──
/** `task_layout_version.scope_kind` (design §3.3): a board's own layout or a space's default. */
export const taskLayoutScope = ["board", "space"] as const;
/**
 * `field_rule.required_on` (design §3.1 "`*` with its stage"): when a value must be there.
 * BOARD-GATES: `enter` on entering one of the rule's `required_statuses` (a Concept's pain point).
 */
export const fieldRequiredOn = ["none", "create", "complete", "both", "enter"] as const;

// ── Parity P2 (conversations, inbox, memory) ──
// (wave 1 additions go here)

// ── Parity P3a (views, order) ──
// (wave 1 additions go here)

// ── Parity P3b (board settings, fields, dashboard, create) ──
// (wave 1 additions go here)

// ── Parity P4 (lifecycle, access, structure) ──
/** `access_grant.subject_kind` (design §5.3): what the Share panel edits. */
export const accessSubjectKind = ["space", "board", "page"] as const;
/** `access_restriction.subject_kind`: a board or a page may stop inheriting from its space. */
export const accessRestrictableKind = ["board", "page"] as const;
/** `access_grant.principal_kind`: a person membership, a team, or a seat (agent seats too). */
export const accessPrincipalKind = ["person", "team", "seat", "company"] as const;
/** `access_grant.level`, weakest first (the resolver's order; "none" is never stored). */
export const accessGrantLevel = ["view", "comment", "edit", "full"] as const;
/** `access.changed.change`: what an access mutation did. */
export const accessChange = ["granted", "revoked", "restricted", "unrestricted"] as const;

// ── Parity P5 (process engine) ──
/** `automation.scope` (design §6.1): company-wide, or one board (its settings and home channel). */
export const automationScope = ["company", "board"] as const;
/** `automation.source`: written in the builder, drafted from a sentence, or proposed by an agent. */
export const automationSource = ["person", "nl", "agent"] as const;
/**
 * `automation.paused_reason`: why a rule stopped firing without a person turning it off — its
 * owner lost access (re-checked at fire time), or it spent its effect budget for the hour.
 */
export const automationPauseReason = [
  "owner_lost_access",
  "budget_exceeded",
  // T6-BOARD-NOTE: a person paused every rule of the board ("Pause rules" on its note)
  "board_paused",
] as const;
/**
 * `automation_run.outcome`: `fired` every action applied; `partial` some refused or failed after
 * one retry; `refused` nothing ran (loop depth, budget, owner access); `skipped` the conditions
 * did not hold (not recorded, except for schedule triggers' history).
 */
export const automationRunOutcome = ["fired", "partial", "refused", "skipped"] as const;
/** `task_due_reminder.kind`: the 24-hour reminder and the daily overdue one. */
export const taskDueReminderKind = ["due_soon", "overdue"] as const;
/** `recurring_control.takeover_scope` (design §6.2): this occurrence only, or all future ones. */
export const recurringTakeoverScope = ["occurrence", "all"] as const;
/** `recurring_run.outcome`: what became of one occurrence (or a control applied to the series). */
export const recurringRunOutcome = [
  "materialised",
  "skipped",
  "paused",
  "resumed",
  "taken_over",
] as const;

// ── Parity P6a (policy, proposals, approvals) ──
/** `agent_policy.scope` (hybrid design §8.1): company rows are the ceiling, board rows tighten. */
export const agentPolicyScope = ["company", "seat", "board"] as const;
/** `agent_policy.mode`: `inherit` defers to the level above; the rest are D3's three verdicts. */
export const agentPolicyMode = ["inherit", "direct", "ask", "never"] as const;
/** DECISION-LEDGER: a stored verdict of the agent policy (`cell.agent_decision`). */
export const agentDecisionVerdict = ["allow_in_run", "propose", "deny"] as const;
/**
 * DECISION-LEDGER: where the verdict was made (a write's own gate, or dispatch). rel/wave33
 * (0263): `rest_call`, a self-hosted DE's REST write refused at its own request (CORE-KEYS'
 * `rest:policy_gate`), was kept as `in_run` before.
 */
export const agentDecisionStage = ["in_run", "tool_call", "run_admission", "rest_call"] as const;
// ── CORE-FREEZE (core plan item 7) ──
/**
 * `incident.kind`: `freeze` a person stopped an agent or the company; the rest are the automatic
 * incidents later builds open (a loop, a budget stop, a guardrail trip, a reversal marked wrong).
 */
export const incidentKind = ["freeze", "loop", "budget", "guardrail", "reversal"] as const;
/** `incident.scope`: the whole company, or one digital employee. */
export const incidentScope = ["company", "digital_employee"] as const;
/** `incident.status`: open while it holds (a freeze holds while open). */
export const incidentStatus = ["open", "resolved"] as const;
/**
 * What a freeze does to queued outward sends (founder decision pending): `hold` keeps them
 * queued and unsent until the freeze is lifted (the safe default); `cancel` cancels them.
 */
export const freezeOutboxMode = ["hold", "cancel"] as const;
/** CORE-PRECEDENTS: `ruling.decision`, the person's decision the ruling records. */
export const rulingDecision = ["approve", "reject"] as const;
/** GMAIL-P1: `mail_header.direction`, a message received or sent. */
export const mailHeaderDirection = ["in", "out"] as const;
/**
 * The agent action classes of design §8.1 (services/api domain/agent-policy/classes.ts holds
 * their defaults and operations). A class not in this list is decided Ask (fail closed).
 */
export const agentActionClass = [
  "read",
  "comment",
  "task_create",
  "assign",
  "field_set",
  "upload",
  "status_own",
  "status_other",
  "complete_other",
  "reorder",
  "task_move",
  "task_delete",
  "restore",
  "file_delete",
  "board_config",
  "automation_write",
  "playbook",
  "page_edit",
  "bulk",
  "external",
  "delegation",
  // CALENDAR-C1: create, move, cancel or RSVP on a calendar event (Ask by default)
  "calendar",
] as const;

// ── WALLET-P1 (prepaid AI credits; docs/design/2026-10-27-wallet-pricing.md) ──
/** `wallet_entry.kind`: money in (topup, bonus, adjustment), money out (usage, refund), fixes. */
// lane D (USAGE-ATTRIBUTION, migration 0246; shared contracts §4.1)
/**
 * `cost_event.usage_category` / `wallet_entry.usage_category`: what a cost row is for. Null on
 * rows written before 0246 (the close worksheet reads them as `work`).
 */
export const usageCategory = [
  "work",
  "voice",
  "system_maintenance",
  "human_time",
  "subscription",
  "adjustment",
] as const;
// end lane D
export const walletEntryKind = [
  "topup",
  "bonus",
  "usage",
  "refund",
  "adjustment",
  "reversal",
] as const;
/** `tariff.scope`: the platform list (mirrored from `cp.tariff`) or a company's own override. */
export const tariffScope = ["platform", "company"] as const;
/** `wallet_checkout.status`: a Stripe Checkout Session the API created, until paid or expired. */
export const walletCheckoutStatus = ["open", "paid", "expired"] as const;
/** `wallet_refund_intent.state`: an operator refund request, until booked or failed at Stripe. */
export const walletRefundIntentState = ["pending", "booked", "failed"] as const;
/** `digital_employee.pause_reason`: why a paused DE is paused (a top-up resumes `wallet` only). */
export const dePauseReason = [
  "manual",
  "budget",
  "wallet",
  "subscription",
  "operator",
  // AGENT-GUARDRAILS: past its hourly write cap; a person resumes it
  "guardrail",
] as const;
/** GUIDED-FLOW: the flows the engine runs (`packages/domain-work/flows`). */
export const flowKey = [
  "seat_setup",
  "de_onboarding",
  // COLLECT-FLOWS (0131): one collection session per person-role.
  "collect_ceo",
  "collect_am",
  "collect_recruiter",
  "collect_sales",
  "collect_finance",
] as const;
/** GUIDED-FLOW: a run is open until submitted (complete) or cancelled. */
export const flowRunStatus = ["open", "completed", "cancelled"] as const;
/** GUIDED-FLOW: what the secure step seals (`cell.sensitive_value`; keyring, never in an event). */
export const sensitiveValueName = [
  "pay_cadence",
  "pay_rate",
  "pay_currency",
  "payout_method",
  "bank_account",
  "id_number",
] as const;

// ── BILLING-PLANS (recurring plans, one-time charges; wallet-pricing design §3.5–§3.7) ──
/** `maintenance_plan.tier`: agent maintenance per DE per month, by band. */
export const maintenanceTier = ["standard", "advanced", "managed"] as const;
/**
 * `maintenance_plan.supervision`: who holds the DE's supervisor seat — the client, or a user of
 * the agency (the platform operator). Migration 0162 rewrote the old `htva` value in place.
 */
export const maintenanceSupervision = ["client", "agency"] as const;
/** `billing_account.collection_method`: card in the app, or an invoice with net terms. */
export const collectionMethod = ["card", "invoice"] as const;
/**
 * BILLING-LEDGER `plan.system_usage` (and `billing_account.system_usage`, nullable): how system
 * usage is billed — charged, covered by the plan, or waived.
 */
export const systemUsageBilling = ["billable", "included", "waived"] as const;
/** `billing_account.payment_methods`: what a sent invoice's hosted page offers. */
export const invoicePaymentMethod = ["card", "us_bank_account", "customer_balance"] as const;
/** `company_subscription.status` (`suspended` is ours: grace ran out; Stripe still says past_due). */
export const subscriptionStatus = [
  "trialing",
  "active",
  "past_due",
  "suspended",
  "cancelled",
] as const;
/** `invoice.status`, Stripe's; Overdue is derived (open and past `due_date`). */
export const invoiceStatus = ["draft", "open", "paid", "uncollectible", "void"] as const;
/** `invoice.source`: what the invoice is for. */
export const invoiceSource = [
  "subscription",
  "one_off",
  "topup",
  "milestone",
  "maintenance",
  "setup",
] as const;
/** `company_discount.applies_to`. */
export const discountAppliesTo = ["plans", "maintenance", "all_recurring"] as const;
/** `quote.status`, Stripe's. */
export const quoteStatus = ["draft", "open", "accepted", "canceled", "expired"] as const;
/** `billing_operation.status`: the operation ledger (domain/billing/operations.ts). */
export const billingOperationStatus = ["queued", "planned", "done", "failed"] as const;
/** `charge_catalog.kind`. */
export const chargeKind = ["setup", "build_milestone", "maintenance", "custom"] as const;
/** `build_project.kind`. */
export const buildProjectKind = ["custom", "complex"] as const;
/** `build_project.status`. */
export const buildProjectStatus = [
  "quoted",
  "accepted",
  "in_progress",
  "delivered",
  "closed",
] as const;
/** `build_project.maintenance_shape`: after delivery, a yearly % of the build or a retainer. */
export const buildMaintenanceShape = ["none", "yearly_pct", "retainer"] as const;
/** `build_milestone.status`. */
export const milestoneStatus = ["pending", "invoiced", "paid"] as const;

// ── MANAGED-POLICY (build order 2026-10-28, increment 1 B) ──
/**
 * `company.agent_mode`: who adds agents (DEs and AGs). `managed`: the company requests one and
 * its agency fulfils it; `self_serve`: the company's managers add them. An agency admin switches.
 */
export const companyAgentMode = ["managed", "self_serve"] as const;
/**
 * `agent_request.kind`: a digital employee (`de`) or an agent (`ag`) in a managed company; or
 * (HELPERS, paid) a new helper for one DE (`helper`), in any company.
 */
export const agentRequestKind = ["de", "ag", "helper"] as const;
/** `agent_request.state`: open until the agency fulfils or declines it. */
export const agentRequestState = ["open", "fulfilled", "declined"] as const;
/**
 * HELPERS (paid helper requests; driven by wp/SKILLSMITH-OPPORTUNITIES): a helper request's
 * stage. `requested` → `quoted` (a price or invoice sent) → `approved` (by the client) → `paid`
 * (subscription updated) → `building` → `added`; `declined` ends it at any open stage. `added`
 * and `declined` close the request (`state` fulfilled / declined).
 */
export const helperRequestStage = [
  "requested",
  "quoted",
  "approved",
  "paid",
  "building",
  "added",
  "declined",
] as const;
/**
 * HELPERS-2: how a DE came to use a helper it does not lead (`agent_use.source`): seeded from its
 * runs' history (`history`), added by a person (`manager`), or declared by the DE itself (`self`).
 */
export const agentUseSource = ["history", "manager", "self"] as const;

// ── Parity P6b (connections, tools, runtime) ──
// (wave 1 additions go here)

// ── Parity P7 (guide, import, goals) ──
// (wave 1 additions go here)

// ── CHAT-SWITCH-B (docs/direction/2026-10-28-chat-slack-switch.md) ──
/** `chat_ask.state`: a DE is answering, has answered, or will not (why in `failure`). */
export const chatAskState = ["working", "answered", "failed"] as const;
/** `scheduled_message.state`: waiting, posted, cancelled by its author, or could not post. */
export const scheduledMessageState = ["scheduled", "sent", "cancelled", "failed"] as const;
/** `link_preview.status`: a fetched card, or a fetch that failed (cached either way). */
export const linkPreviewStatus = ["ok", "failed"] as const;
/** `@channel` / `@here` (a scheduled message's `broadcast`). */
export const chatBroadcast = ["channel", "here"] as const;

// ── COS-SLICE1 (docs/design/2026-10-28-chief-of-staff-and-assistants.md) ──
/** §5.3: what a follow-up card offers (`decision` is phase 2; no card makes one yet). */
export const suggestionKind = ["task", "reminder", "meeting"] as const;
export const suggestionState = ["pending", "accepted", "dismissed", "expired"] as const;
/** §5.2: `rule` is the deterministic detector; `classifier` is phase 2. */
export const suggestionSource = ["rule", "classifier"] as const;
/** §9.1 (5): what an accepted card made (`calendar_event` once the calendar lands). */
export const suggestionResultKind = ["task", "reminder", "meeting"] as const;
/** §2.5: an interval check's digest, or an immediate one (one section). */
export const cosDigestKind = ["interval", "immediate"] as const;

// ── TEMPLATES-T1..T4 (docs/design/2026-10-28-agent-templates.md) ──
/** Decision 2: the three named tiers (models behind them per company policy). */
export const modelTier = ["light", "standard", "heavy"] as const;
/** `template_overlay.scope`: an industry's vertical pack, or one company's overlay. */
export const templateOverlayScope = ["vertical", "company"] as const;
/** `cp.template_vertical_pack.status`. */
export const verticalPackStatus = ["published", "deprecated"] as const;
/** A family's access: read, act with approval, act directly, manage; `none` closes it. */
export const accessLevel = ["R", "A", "D", "M", "none"] as const;
/** `de_access_default.source`: which layer the DE's level came from (the diff's key). */
export const accessDefaultSource = ["template", "overlay", "manual"] as const;
/** The access families of skills and roles (domain-agents `SKILL_ACCESS_FAMILIES`). */
export const accessFamily = [
  "mail",
  "calendar",
  "message",
  "crm",
  "ledger",
  "social",
  "intake",
  "docs",
  "board",
  "chat",
  "kpis",
  "web",
  "recall",
  // HTVA-AUDIT P4: the render service and an image integration, reached through a connection.
  "render",
  "image",
] as const;
// ── REACH-S1 (docs/design/2026-10-28-agent-reach.md §2, slice R1) ──
/** `agent_reach.membership_kind`: the agents a reach belongs to (a DE or a personal agent). */
export const agentReachMemberKind = ["digital_employee", "personal_agent", "system_agent"] as const;
/** `agent_reach.serves_kind`: whom the agent serves (a person, a seat, a team, the company). */
export const agentReachServesKind = ["person", "seat", "team", "company"] as const;
/** `agent_reach_request.state`: a person's ask to talk to an agent outside its reach. */
export const agentReachRequestState = ["pending", "granted", "declined"] as const;
// ── SHARED-DES (docs/design/2026-10-30-shared-des-and-fulfillment.md §2.2) ──
/** `seat_serves.target_kind`: what a shared seat serves beside its home line. */
export const seatServesTargetKind = ["seat", "team", "company"] as const;
// ── MULTI-SPACE-ACCESS: an item shared into more spaces than its home ──
/** `space_share.object_type`: what is shared into another space. */
export const spaceShareObjectType = ["channel", "page"] as const;
/** `space_share.level`: the space role the shared space's members get on the item (05 `space`). */
export const spaceShareLevel = ["owner", "editor", "viewer", "reader"] as const;
// ── KB-UPLOAD-2: a doc shared with people and DEs directly; a true read-only level ──
/**
 * A doc's levels, weakest first: `reader` reads only (no comments, no edits; a space share's
 * `reader` too), `viewer` reads and comments, `editor` edits. Stored as `access_grant` levels
 * `view` / `comment` / `edit` for a person or seat.
 */
export const pageMemberLevel = ["reader", "viewer", "editor", "manager"] as const;
// SHARE-EVERYONE: `manager` is "Full access" (stored as the grant level `full`): it edits, shares
// and manages the doc. `company` (principal_id = the company id) is every member and agent of it.
/** Who a doc is shared with directly: a person (membership) or a seat (a DE's, or a person's). */
export const pageMemberPrincipalKind = ["person", "seat", "company"] as const;
// (`structure.changed` kinds: `structureChangeKind`, defined once under STRUCTURE-CHANGED.)
// ── SCREENS-S1 (docs/design/2026-10-28-screens-stack-contract.md) ──
/** `screen_stack.state`: `stale` = no `stack.health` for 180 s (new leases queue). */
export const screenStackState = ["live", "stale", "retired"] as const;
export const screenStackPlacement = ["shared", "dedicated"] as const;
/** How a browser lease is watched: a VNC stream (default) or a screenshot stream (fallback). */
export const browserLiveMode = ["vnc", "screenshot"] as const;
/** `browser_account.status` (browser-accounts §3). */
export const browserAccountStatus = ["live", "needs_you", "expired", "revoked"] as const;
/**
 * PERSISTENT-SCREENS: what a saved profile keeps — a browser's session (cookies, storage) or a
 * desktop's home volume (app logins). Both live on the stack, encrypted at rest, never here.
 */
export const browserAccountKind = ["browser", "desktop"] as const;
/** What a grant lets its subject do with the account (browser-accounts §3). */
export const browserAccountGrantLevel = ["use_read", "use_act", "manage"] as const;
/** Who a grant names: a DE (its membership) or a seat. */
export const browserAccountGrantSubject = ["membership", "seat"] as const;
export const screenLeaseKind = ["browser", "desktop"] as const;
/** A lease: waiting in the queue, live (agent, person driving, waiting on a person), or over. */
export const screenLeaseState = ["queued", "active", "taken_over", "needs_you", "ended"] as const;
/** Who holds a lease: a DE's run, an AG (a delegated worker run), or a person. */
export const screenLeaseHolder = ["de", "ag", "person"] as const;
/** Queue order: person-initiated before scheduled (decision §7.4). */
export const screenLeaseOrigin = ["person", "agent"] as const;
/** `task`: an agent's, for its task; `login`: a person logs in to their own account for agents; `adhoc`: an admin's. */
export const screenLeasePurpose = ["task", "login", "adhoc"] as const;
export const screenLeaseEndReason = [
  "released",
  "task_ended",
  "idle",
  "stopped",
  "stack_gone",
  "failed",
] as const;
/** MONITORING-P1: a telemetry event's level (`telemetry_event.level`). */
export const telemetryLevel = ["debug", "info", "warn", "error"] as const;
/** MONITORING-P1: a detached job's state (`monitor_job.status`), from its own events. */
export const monitorJobStatus = ["running", "done", "failed", "cancelled"] as const;
/** MONITORING-P1: what an alert is about (`monitor_alert.kind`). */
export const monitorAlertKind = [
  "stuck_run",
  "post_storm",
  "job_stalled",
  "guard_tripped",
  // MONITORING-P2: a DE whose telemetry is on went quiet while it has runs to do.
  "bridge_silent",
  // SKILL-LEAK-GUARD: an agent's outgoing text matched a protected skill (canary or overlap).
  "skill_leak_suspected",
  // PROBE-DETECTOR: 3+ (warning) or 6+ (critical) messages in one thread that look like attempts
  // to extract a DE's instructions or skills. Alert-only.
  "extraction_probe",
  // FAILURE-STOP: the MCP server stopped a run after repeated tool failures.
  "repeated_failures",
  // lane B (REQUEST-RECOVERY, 0245): a request is interrupted and waits on a person (one per request).
  "recovery",
  // TASK-BUDGETS: a run hit its per-task limit (steps, cost or time) and was stopped.
  "task_budget",
] as const;
/** SKILL-LEAK-GUARD: what the skill-leak guard does on a hit (`settings.security.skill_guard_mode`). */
export const skillGuardMode = ["alert", "block_and_alert"] as const;
/**
 * PROBE-DETECTOR: what an extraction probe looks like (`probe_flag.category`), the attack families
 * of docs/research/2026-11-01-prompt-extraction-defense.md §2.
 */
export const probeCategory = [
  "direct_ask",
  "process_framing",
  "persona_swap",
  "debug_mode",
  "encoding",
  "translation",
  "creative_format",
  "piecemeal",
  "summarise",
  "repeat_above",
  "completion",
  "export_channel",
  "injection",
  "ignore_previous",
  "emotional_pressure",
  "authority_claim",
  "legal_demand",
  "teach_course",
  "similar_skill",
] as const;
/** PROBE-DETECTOR: who settled a flag's score: the patterns, or the judge on a borderline one. */
export const probeFlagSource = ["patterns", "judge"] as const;
export const monitorAlertSeverity = ["warning", "critical"] as const;
/** Open until it clears by itself or a manager resolves it; `acked` = someone is on it. */
export const monitorAlertState = ["open", "acked", "resolved"] as const;
/**
 * MONITORING-P1: a stop asked of a run, carried by the DE's bridge (`run_stop_request.state`):
 * `pending` until the bridge polls, `picked` once it has, then `acked` (stopped) or `failed`
 * (the bridge could not stop it); `expired` when no bridge picked it within 5 minutes.
 */
export const runStopRequestState = ["pending", "picked", "acked", "failed", "expired"] as const;
/**
 * Who asked: a person (the Stop button) or a guard rule (`stop_and_alert`). SKILLSMITH-LENS:
 * `operator`, a platform super admin's one click from Skillsmith (no company membership; the
 * audit row names the person).
 */
export const runStopRequestSource = ["person", "guard", "operator"] as const;
/**
 * MONITORING-P1: what a guard rule matches (`guard_rule.match_kind`): a thread id (posted to or,
 * GUARDS-ARGS, read), a tool name, or `tool_arg`: the sanitised argument summary of a
 * terminal-like call (`curl camofox:9377/tabs/…/evaluate`: command names, URL hosts and paths).
 */
export const guardMatchKind = ["thread", "tool", "tool_arg"] as const;
/** A guard's action: open an alert, or stop the run and open an alert. */
export const guardAction = ["alert", "stop_and_alert"] as const;
/** Why an agent asked for a person (`screens_act needs_you`). */
export const screenNeedsYouWhy = ["login", "2fa", "captcha", "other"] as const;
/** A needs-you ask's card: open, then done (handed back), not now, expired, or ended with its screen. */
export const screenAskState = ["open", "done", "declined", "expired", "ended"] as const;
/**
 * BOARD-RESULTS: who said when a published card went out (`post_publication.source`): the
 * results sweep's estimate (the time it entered Published), a person, or a social connector.
 */
export const postPublicationSource = ["estimated", "manual", "connector"] as const;
/**
 * ONBOARDING-P1: where a client's onboarding case is. `link_sent` from creation (the link went
 * out, or is waiting for the operator to send it), `in_progress` once the owner saved an intake
 * step, `intake_complete` at submit (the follow-up sequence stops there).
 */
export const onboardingCaseStatus = [
  "link_sent",
  "in_progress",
  "intake_complete",
  // ONBOARDING-P2: onboarding ended (the operator closed it); the setup assistant's access ends.
  "live",
  "cancelled",
] as const;
/** ONBOARDING-P1: how the onboarding link (or a follow-up) left: mailed, held for the operator, failed. */
export const onboardingDelivery = ["sent", "would_send", "failed"] as const;
/**
 * ONBOARDING-P1: the follow-up sequence, in business days after the link went out (design §1.3):
 * day 2 a friendly reminder, day 4 exactly what is still missing, day 6 an offer of a call, then
 * an alert to the operator.
 */
export const onboardingFollowupKind = [
  "reminder",
  "missing_list",
  "call_offer",
  "operator_alert",
] as const;
/** ONBOARDING-P1: a follow-up's state (`skipped`: the intake was complete before it was due). */
export const onboardingFollowupStatus = [
  "scheduled",
  "sent",
  "would_send",
  "failed",
  "skipped",
] as const;
/** ONBOARDING-P1: the case's timeline (`cell.onboarding_event`), append-only. */
export const onboardingEventKind = [
  "case_opened",
  "link_sent",
  "link_would_send",
  "link_failed",
  "intake_saved",
  "intake_completed",
  "followup_sent",
  "followup_would_send",
  "followup_failed",
  "followup_skipped",
  "operator_alerted",
  // ONBOARDING-P2
  "proposal_drafted",
  "proposal_draft_failed",
  "proposal_edited",
  "proposal_sent",
  "seat_decided",
  "assistant_assigned",
  "assistant_revoked",
  "intake_moved",
  "case_closed",
  // ONBOARDING-P3
  "proposal_presented",
  "call_offered",
  "proposal_cost_booked",
  "seating_planned",
  "seat_deployed",
  "seat_ticketed",
  "seat_refused",
  "seat_blocked",
  "seat_failed",
  "seat_progressed",
  // lane A (beta shared contracts §3.2): readiness evidence, each written once (`dedupe_key`)
  "owner_access_verified",
  "seat_ready",
  "spec_applied",
  "connection_verified",
  "connection_failed",
  "intake_readable",
  "first_task_delivered",
  "ready",
  "handover",
  // ONBOARDING-P4: after go-live
  "system_address_opened",
  "client_message",
  "review_scheduled",
  "review_collected",
  "review_reviewed",
  "de_provision_requested",
  // AUTO-PROVISION: the DE's container, asked of ClawPanel (readiness evidence)
  "de_container_running",
  "de_container_enrolled",
  "de_container_configured",
  "de_container_failed",
] as const;
/**
 * AUTO-PROVISION: where a DE's container order stands (`cell.de_runtime_order`). `requested`:
 * asked of ClawPanel (or about to be); `creating`: ClawPanel took it; `running`: the container is
 * up; `configured`: CompanyOS configured it (the configure seam); `failed`: ClawPanel failed it
 * or it timed out (an operator Inbox item); `destroyed`: ClawPanel reports it gone (a new
 * generation may then be ordered).
 */
export const deRuntimeOrderState = [
  "requested",
  "creating",
  "running",
  "configured",
  "failed",
  "destroyed",
] as const;
/** AUTO-PROVISION: the only model tiers a provisioned container may run (Andrei, 2026-11-03). */
export const deRuntimeModelTier = ["deepseek-flash", "herko"] as const;
/**
 * ONBOARDING-P4 (design §3): an HQ dispatch item — one message to a client's system address —
 * where it came from: a mail through the inbound mail provider, a post in the client's support
 * channel, or the test injection path (no mail provider configured).
 */
export const dispatchItemSource = ["mail", "support_post", "test_injection"] as const;
/** ONBOARDING-P4: what the HQ dispatcher made of a message (rules, no model). */
export const dispatchItemClass = [
  "support_question",
  "change_request",
  "incident",
  "noise",
] as const;
/**
 * ONBOARDING-P4: where it went. `operator`: the client's assigned operator (and the operator
 * inbox); `quarantine`: held for a person, no agent with client access sees it; `logged`: noise.
 */
export const dispatchItemRoute = ["operator", "quarantine", "logged"] as const;
/** ONBOARDING-P4: why a message is held in quarantine. */
export const dispatchQuarantineReason = [
  "unknown_address",
  "unknown_sender",
  "unverified_sender",
  "screen_flagged",
] as const;
/** ONBOARDING-P4: the post-go-live review cases (`cell.onboarding_review.kind`). */
export const onboardingReviewKind = ["day_30"] as const;
/**
 * ONBOARDING-P4: a review's state. `scheduled` at the handover (due 30 days later); `collected`
 * once the job filled it (KPIs per seat, failures, usage cost, open recoveries); `reviewed` once
 * the operator went through it.
 */
export const onboardingReviewStatus = ["scheduled", "collected", "reviewed"] as const;
/**
 * Lane A (beta shared contracts §3.2): the six checks a case is ready for handover on
 * (`onboarding_case.readiness`), in the order the console shows them.
 */
export const onboardingReadinessCheck = [
  "owner_access",
  "seats",
  "spec",
  "connections",
  "intake",
  "first_task",
] as const;
/**
 * ONBOARDING-P2: a setup proposal version (`cell.setup_proposal.status`). `draft` while the
 * operator reviews it (the client never sees one); `sent` once the operator froze it for the
 * client (immutable from then on); `superseded` when a later version was sent.
 */
export const setupProposalStatus = ["draft", "sent", "superseded"] as const;
/** ONBOARDING-P2: who wrote a proposal version: the behind-the-scenes draft, or an operator. */
export const setupProposalOrigin = ["system_draft", "operator"] as const;
/** ONBOARDING-P2: a proposed seat is a person's or a digital employee's. */
export const setupSeatKind = ["human", "de"] as const;
/** ONBOARDING-P2: when a proposed seat starts: in the plan now, or on an upgrade. */
export const setupSeatStart = ["now", "on_upgrade"] as const;
/** ONBOARDING-P2: the owner's answer on one proposed seat (`cell.setup_seat_decision`). */
export const setupSeatDecision = ["approve", "change", "not_now"] as const;
/** ONBOARDING-P2: an HQ agent's assignment to a client (`cp.agent_assignment.role`). */
export const agentAssignmentRole = ["onboarding"] as const;
/** ONBOARDING-P2: why an assignment ended: the client went live, onboarding was cancelled, reassigned. */
export const agentAssignmentEndReason = ["live", "cancelled", "reassigned"] as const;
/** ONBOARDING-P2: an HQ setup assistant's state (`cp.setup_assistant.status`). */
export const setupAssistantStatus = ["active", "retired"] as const;
/**
 * ONBOARDING-P3: one approved seat in the seating run (`cell.onboarding_seating.status`).
 * `pending` until the runner reaches it; `deploying` once its DE exists and its later steps run;
 * `deployed` (a person's seat is open; a DE is live, its first task still to be delivered);
 * `ready` (a person holds the seat; the DE delivered its first task: the readiness job sets it);
 * `ticket` (no fitting template: a template ticket was opened instead of an improvised DE);
 * `refused` (past the plan's DE allowance, or the owner no longer approves it); `blocked` (no person
 * holds a seat to report to yet); `failed` (three tries failed; the message says why).
 */
export const onboardingSeatingStatus = [
  "pending",
  "deploying",
  "deployed",
  "ready",
  "ticket",
  "refused",
  "blocked",
  "failed",
] as const;
/**
 * ONBOARDING-P3 (recovery contract, beta shared contracts §3.3): the NEXT step of one seat, each
 * step committed on its own. `seat` (its seat, and for a DE seat the DE in the same transaction:
 * the allowance place and the setup fee are taken there, once) → `provision` (the DE's set-up) →
 * `connect` (its runtime connection) → `activate` (the DE goes live) → `first_task` (its check-in
 * task delivered) → `ready`. `de` is a DE seat whose seat exists and whose DE does not. A
 * person's seat goes `seat` → `ready`. A retry resumes at `step`; a step never goes back.
 */
export const onboardingSeatingStep = [
  "seat",
  "de",
  "provision",
  "connect",
  "activate",
  "first_task",
  "ready",
] as const;
/** ONBOARDING-P3: a template ticket (`cell.template_ticket.status`). */
export const templateTicketStatus = ["open", "done", "dismissed"] as const;

// ── lane B: REQUEST-IDENTITY (0244) and REQUEST-RECOVERY (0245) ──
// docs/design/2026-11-02-beta-shared-contracts.md §1, §2.
/** Where a request (`cell.request`) came from. */
export const requestOrigin = [
  "chat",
  "task",
  "voice",
  "schedule",
  "onboarding",
  "helper",
  "import",
] as const;
/**
 * A request's state: `open` (worked on) → `delivered` (the DE handed in a result for review) →
 * `accepted` (a person approved it complete) | `rejected`; `cancelled` (dismissed or withdrawn).
 * Rework moves `delivered` back to `open` with `revision + 1`.
 */
export const requestState = ["open", "delivered", "accepted", "rejected", "cancelled"] as const;
/** How a request was decided: a board review, the chat delivery card, or automatically. */
export const requestDecidedVia = ["review", "chat", "auto"] as const;
/** The subject a request is about (the helper chain vocabulary). */
export const requestSubjectKind = ["message", "task", "topic"] as const;
/** Why a request is interrupted (`cell.request_recovery.cause`). */
export const recoveryCause = [
  "repeated_failures",
  "loop",
  "runtime_lost",
  "timed_out",
  "start_failed",
  "parked",
  "helper_stalled",
  "helper_crashed",
  "stop_requested",
  "commitment_failed",
  "delivery_refused",
  // TASK-BUDGETS: the run hit its per-task cost limit or its tool-step cap (a time limit is
  // `timed_out`).
  "budget_exhausted",
  "step_cap",
] as const;
/** A recovery's state: `open` until a person acts (or the request ends by itself). */
export const recoveryState = [
  "open",
  "continued",
  "taken_over",
  "dismissed",
  "auto_resolved",
] as const;
/** Which step of the accountable walk answered (`accountableFor`). */
export const recoveryAccountableVia = [
  "holder",
  "supervisor",
  "owner",
  "delegate",
  "next_up",
] as const;
/** The actions on a recovery (`POST recoveries/{id}/resolve`). */
export const recoveryAction = ["continue", "take_over", "dismiss"] as const;
